import React, { useEffect, useMemo, useRef, useState, useCallback, useImperativeHandle } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Play, Pause, Trash2, Loader2, Sparkles, Film, Library, Volume2, VolumeX,
  Music, Type as TypeIcon, Image as ImageIcon, ZoomIn, ZoomOut,
  AlignLeft, AlignCenter, AlignRight, Bold, Italic, Palette, Scissors,
  Save, FolderOpen, X, Mic, Plus, Keyboard
} from 'lucide-react';
import toast from 'react-hot-toast';
import { videoStudioService } from '../../services/videoStudioService';
import {
  GeneratedVideo, VideoScene,
  AnyClip, VideoClip, AudioClip, TextOverlayClip, ImageOverlayClip,
  CaptionTrack, CaptionCue, FontFamily
} from '../../types/videoStudio';
import MediaLibrary from './MediaLibrary';
import CaptionsEditor from './CaptionsEditor';
import GalleryPicker, { GalleryAsset } from './GalleryPicker';
import TransitionPicker from './TransitionPicker';
import AIEditChat from './AIEditChat';

// =============================================================================
// CONSTANTS
// =============================================================================

const FILTER_PRESETS: { id: string; label: string; css: string }[] = [
  { id: 'none', label: 'Original', css: 'none' },
  { id: 'cinematic', label: 'Cinema', css: 'contrast(1.15) saturate(1.2) brightness(0.95)' },
  { id: 'warm', label: 'Warm', css: 'sepia(0.2) saturate(1.3) hue-rotate(-10deg)' },
  { id: 'cool', label: 'Cool', css: 'saturate(1.1) hue-rotate(180deg) brightness(1.05)' },
  { id: 'vintage', label: 'Vintage', css: 'sepia(0.5) contrast(0.9) brightness(0.95)' },
  { id: 'mono', label: 'Mono', css: 'grayscale(1) contrast(1.1)' }
];

const FONT_FAMILIES: FontFamily[] = ['Inter', 'Arial', 'Georgia', 'Courier New', 'Impact', 'Comic Sans MS'];

// Quick one-click style presets for the text overlay panel.
const TEXT_PRESETS: { key: 'presetTitle' | 'presetTag' | 'presetHeadline' | 'presetQuote' | 'presetCta' | 'presetWarning'; props: Partial<TextOverlayClip> }[] = [
  { key: 'presetTitle',    props: { font_family: 'Inter',   font_size: 72, bold: true,  italic: false, color: '#FFFFFF', background: 'transparent',           position: 'center', position_x_pct: 50, position_y_pct: 50 } },
  { key: 'presetTag',      props: { font_family: 'Inter',   font_size: 40, bold: true,  italic: false, color: '#FFFFFF', background: 'rgba(0,0,0,0.85)',      position: 'top',    position_x_pct: 50, position_y_pct: 10 } },
  { key: 'presetHeadline', props: { font_family: 'Impact',  font_size: 96, bold: true,  italic: false, color: '#FFFF00', background: 'rgba(0,0,0,0.6)',       position: 'bottom', position_x_pct: 50, position_y_pct: 88 } },
  { key: 'presetQuote',    props: { font_family: 'Georgia', font_size: 48, bold: false, italic: true,  color: '#FFFFFF', background: 'transparent',           position: 'center', position_x_pct: 50, position_y_pct: 50 } },
  { key: 'presetCta',      props: { font_family: 'Inter',   font_size: 56, bold: true,  italic: false, color: '#FFFFFF', background: 'rgba(168,85,247,0.9)',  position: 'bottom', position_x_pct: 50, position_y_pct: 88 } },
  { key: 'presetWarning',  props: { font_family: 'Arial',   font_size: 56, bold: true,  italic: false, color: '#000000', background: 'rgba(255,221,0,0.95)',  position: 'top',    position_x_pct: 50, position_y_pct: 10 } }
];

// Common text/background swatches for one-click colour picking.
const COLOR_SWATCHES = ['#FFFFFF', '#000000', '#FFFF00', '#FF3B30', '#34C759', '#0A84FF', '#AF52DE', '#FF9500'];

const parseBackground = (bg?: string): { hex: string; opacity: number; enabled: boolean } => {
  if (!bg || bg === 'transparent') return { hex: '#000000', opacity: 0.5, enabled: false };
  const m = bg.match(/rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*(?:,\s*([\d.]+))?\s*\)/);
  if (m) {
    const toHex = (v: string) => Number(v).toString(16).padStart(2, '0');
    return { hex: `#${toHex(m[1])}${toHex(m[2])}${toHex(m[3])}`.toUpperCase(), opacity: m[4] !== undefined ? Number(m[4]) : 1, enabled: true };
  }
  if (/^#[0-9a-fA-F]{6}$/.test(bg)) return { hex: bg.toUpperCase(), opacity: 1, enabled: true };
  return { hex: '#000000', opacity: 0.5, enabled: true };
};

const buildBackground = (hex: string, opacity: number): string => {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r},${g},${b},${Math.max(0, Math.min(1, opacity))})`;
};

// font_size is authored at a 720-tall reference frame. Preview + export scale
// font_size by (rendered_height / 720) so the same number always renders at the
// same proportion on screen, regardless of preview or export resolution.
const FONT_REFERENCE_HEIGHT = 720;

// Caption position → centre Y (%) mapping. Used by both the preview overlay and
// the canvas exporter so a caption that looks like it's at the bottom on screen
// is at the bottom in the rendered file, at any resolution.
const CAPTION_POSITION_Y_PCT: Record<string, number> = {
  top: 8,
  center: 50,
  bottom: 92
};

// Resolve a TextOverlayClip's centre coords. position_*_pct are authoritative;
// when missing (legacy clips), derive from the legacy 'position' enum.
const getTextCoords = (clip: TextOverlayClip): { x: number; y: number } => ({
  x: clip.position_x_pct ?? 50,
  y: clip.position_y_pct ?? (clip.position === 'top' ? 10 : clip.position === 'center' ? 50 : 90)
});

const ASPECT_DIM: Record<string, { w: number; h: number }> = {
  '16:9': { w: 1280, h: 720 },
  '9:16': { w: 720, h: 1280 },
  '1:1':  { w: 720, h: 720 },
  '4:3':  { w: 960, h: 720 }
};

// Teto de segurança para valores de tempo digitados/importados. Bate com o
// limite de duração do render no servidor (10 min) com folga para edição.
const MAX_TIMELINE_SECONDS = 3600;
// Nunca desenhar mais que isto de linhas de grade/ticks, senão zoom+duração
// grande viram centenas de milhares de nós no DOM e a aba congela.
const MAX_GRID_LINES = 400;

const TRACK_HEIGHT = 56;
const TRACK_HEADER_WIDTH = 110;
const RULER_HEIGHT = 28;
const SNAP_THRESHOLD_PX = 12;
const MIN_CLIP_DURATION = 0.2;

const newId = () => Math.random().toString(36).slice(2, 10);

const fmtTime = (s: number) => {
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  const cs = Math.floor((s % 1) * 10);
  return `${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}.${cs}`;
};

// Find the lowest free lane index for a new clip [start..end] across one or more
// clip lists — treats them as if they share the same lane numbering. Used to
// stack videos AND overlays together on a single "visual" track.
const nextFreeLane = (
  clipLists: { start: number; duration: number; lane?: number }[][],
  start: number, end: number,
  excludeId?: string
): number => {
  const all = clipLists.flat().filter(c => (c as any).id !== excludeId);
  for (let lane = 0; lane < 9999; lane++) {
    const overlaps = all.some(c => (c.lane || 0) === lane && c.start < end && c.start + c.duration > start);
    if (!overlaps) return lane;
  }
  return 0;
};

const lanesUsed = (...clipLists: { lane?: number }[][]) =>
  Math.max(1, ...clipLists.flat().map(c => (c.lane || 0) + 1));

// Wrap external S3 URLs through our backend proxy so canvas.drawImage / Web Audio
// can read them without CORS issues. Same-origin URLs are passed through unchanged.
const corsSafeUrl = (url: string): string => {
  if (!url) return url;
  const apiBase = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
  const token = localStorage.getItem('token') || '';
  // Only proxy S3 — leave blob:/data:/local URLs alone
  if (!/amazonaws\.com\//i.test(url)) return url;
  return `${apiBase}/video-studio/proxy?url=${encodeURIComponent(url)}&token=${encodeURIComponent(token)}`;
};

// Apply a clip's transition onto a 2D canvas context for the duration of the
// overlay/text/image's first `transition_duration` seconds. Mirrors the CSS
// transitions in `computeTransitionStyle` so preview and export look the same.
// Caller must ctx.save() before and ctx.restore() after, then ctx.filter / alpha
// will be reset. cx/cy is the centre of the overlay's bounding box; bboxW/bboxH
// is its size in canvas pixels. Returns the alpha that should be applied (the
// caller may need to multiply it by an existing per-clip opacity).
const applyOverlayTransitionToCanvas = (
  ctx: CanvasRenderingContext2D,
  clip: { transition?: string; transition_duration?: number; start: number },
  tt: number,
  cx: number, cy: number,
  bboxW: number, bboxH: number
): { alpha: number; filter: string | null } => {
  if (!clip.transition || clip.transition === 'cut') return { alpha: 1, filter: null };
  const dur = clip.transition_duration ?? 0.5;
  const since = tt - clip.start;
  if (since < 0 || since >= dur) return { alpha: 1, filter: null };
  const t01 = Math.max(0, Math.min(1, since / dur));
  const inv = 1 - t01;
  let alpha = 1;
  let txFilter: string | null = null;
  switch (clip.transition) {
    case 'fade':
      alpha = t01; break;
    case 'slide':
      ctx.translate(-bboxW * inv, 0); break;
    case 'wipe':
      ctx.beginPath();
      ctx.rect(cx - bboxW / 2, cy - bboxH / 2, bboxW * t01, bboxH);
      ctx.clip();
      break;
    case 'zoom':
      alpha = t01;
      ctx.translate(cx, cy);
      ctx.scale(0.2 + 0.8 * t01, 0.2 + 0.8 * t01);
      ctx.translate(-cx, -cy);
      break;
    case 'blur':
      alpha = t01;
      txFilter = `blur(${inv * 20}px)`;
      break;
    case 'spin':
      alpha = t01;
      ctx.translate(cx, cy);
      ctx.rotate(inv * -Math.PI);
      ctx.scale(0.5 + 0.5 * t01, 0.5 + 0.5 * t01);
      ctx.translate(-cx, -cy);
      break;
    case 'flip':
      alpha = t01;
      ctx.translate(cx, cy);
      // 2D approximation of a Y-axis flip — horizontal squeeze 0 → 1
      ctx.scale(t01, 1);
      ctx.translate(-cx, -cy);
      break;
    case 'iris':
      ctx.beginPath();
      ctx.arc(cx, cy, t01 * Math.hypot(bboxW, bboxH) / 2, 0, Math.PI * 2);
      ctx.clip();
      break;
  }
  return { alpha, filter: txFilter };
};

// Compute the live transition style for a video clip during the first
// `transition_duration` seconds of its lifetime. Returns merged filter (combining
// the base color filter with any blur tx), opacity, transform, clipPath. For the
// preview we drive this off currentTime instead of CSS animations so it stays
// in sync with seeking and pause.
const computeTransitionStyle = (
  clip: { transition?: string; transition_duration?: number; start: number },
  currentTime: number,
  baseFilter: string
): { filter?: string; opacity?: number; transform?: string; clipPath?: string } => {
  if (!clip.transition || clip.transition === 'cut') return {};
  const dur = clip.transition_duration ?? 0.5;
  const elapsed = currentTime - clip.start;
  if (elapsed < 0 || elapsed >= dur) return {};
  const p = Math.max(0, Math.min(1, elapsed / dur));
  const inv = 1 - p;
  switch (clip.transition) {
    case 'fade':  return { opacity: p };
    case 'slide': return { transform: `translateX(${inv * -100}%)` };
    case 'wipe':  return { clipPath: `inset(0 ${inv * 100}% 0 0)` };
    case 'zoom':  return { transform: `scale(${0.2 + 0.8 * p})`, opacity: p };
    case 'blur': {
      const blurFilter = `blur(${inv * 20}px)`;
      const filter = baseFilter && baseFilter !== 'none' ? `${baseFilter} ${blurFilter}` : blurFilter;
      return { filter, opacity: p };
    }
    case 'spin':  return { transform: `rotate(${inv * -180}deg) scale(${0.5 + 0.5 * p})`, opacity: p };
    case 'flip':  return { transform: `rotateY(${inv * 90}deg)`, opacity: p };
    case 'iris':  return { clipPath: `circle(${p * 75}% at 50% 50%)` };
    default:      return {};
  }
};

// =============================================================================
// MAIN COMPONENT
// =============================================================================

interface Props {
  scenes: VideoScene[];
  onChange: (scenes: VideoScene[]) => void;
  onExported: (video: GeneratedVideo) => void;
  aspectRatio?: string;
  onAspectRatioChange?: (ratio: string) => void;
  serverRenderAvailable?: boolean;
}

// A página adiciona mídia da galeria por aqui em vez de remontar o editor com
// uma lista de `scenes`: a remontagem descartava áudio, textos, imagens,
// legendas, trims e posições (só clipes de vídeo sobreviviam) e ainda soltava o
// projeto do auto-save.
export interface TimelineEditorHandle {
  addAsset: (asset: GeneratedVideo) => void;
}

type Selection = { kind: 'video' | 'audio' | 'overlay'; id: string } | null;

const TimelineEditor = React.forwardRef<TimelineEditorHandle, Props>(({ scenes, onChange, onExported, aspectRatio = '16:9', onAspectRatioChange, serverRenderAvailable = false }, ref) => {
  const { t } = useTranslation();
  const dim = ASPECT_DIM[aspectRatio] || ASPECT_DIM['16:9'];

  // ---- State: tracks ----
  const [videoClips, setVideoClips] = useState<VideoClip[]>(() => {
    let cum = 0;
    return scenes.map(s => {
      const start = cum; cum += s.duration || 5;
      return {
        id: s.id, kind: 'video' as const,
        source_video_id: s.source_video_id, url: s.url || '',
        thumbnail_url: s.thumbnail_url, start, duration: s.duration || 5,
        muted: false, volume: 1, transition: s.transition,
        filter: (s as any).filter || 'none'
      };
    });
  });
  const [audioClips, setAudioClips] = useState<AudioClip[]>([]);
  const [overlayClips, setOverlayClips] = useState<(TextOverlayClip | ImageOverlayClip)[]>([]);
  const [captionTracks, setCaptionTracks] = useState<CaptionTrack[]>([]);

  // Sync back to parent so list-saving still works
  useEffect(() => {
    onChange(videoClips.map(v => ({
      id: v.id, type: 'video' as const, source_video_id: v.source_video_id,
      url: v.url, thumbnail_url: v.thumbnail_url, duration: v.duration,
      transition: v.transition, ...(v.filter ? { filter: v.filter } as any : {})
    })));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoClips]);

  const totalDuration = useMemo(() => {
    const all = [...videoClips, ...audioClips, ...overlayClips];
    return Math.max(1, all.reduce((acc, c) => Math.max(acc, c.start + c.duration), 0));
  }, [videoClips, audioClips, overlayClips]);

  // ---- Undo / Redo (history stack) ----
  // Inclui captionTracks: apagar um bloco de legendas (minutos de transcrição
  // paga) era irrecuperável porque o snapshot só cobria vídeo/áudio/overlay.
  type Snapshot = { vc: VideoClip[]; ac: AudioClip[]; oc: (TextOverlayClip | ImageOverlayClip)[]; ct: CaptionTrack[] };
  const historyRef = useRef<Snapshot[]>([]);
  const historyIdxRef = useRef<number>(-1);
  const restoringRef = useRef(false);

  const pushHistory = useCallback((vc: VideoClip[], ac: AudioClip[], oc: (TextOverlayClip | ImageOverlayClip)[], ct: CaptionTrack[]) => {
    if (restoringRef.current) return;
    // Truncate redo stack if we're branching off
    historyRef.current = historyRef.current.slice(0, historyIdxRef.current + 1);
    historyRef.current.push({
      vc: JSON.parse(JSON.stringify(vc)),
      ac: JSON.parse(JSON.stringify(ac)),
      oc: JSON.parse(JSON.stringify(oc)),
      ct: JSON.parse(JSON.stringify(ct))
    });
    if (historyRef.current.length > 50) historyRef.current.shift();
    historyIdxRef.current = historyRef.current.length - 1;
  }, []);

  // Zera a pilha ao trocar de projeto. Sem isso, Ctrl+Z logo após abrir o
  // projeto B restaurava o snapshot do projeto A — e o auto-save gravava o
  // conteúdo de A dentro de B.
  const resetHistory = useCallback(() => {
    historyRef.current = [];
    historyIdxRef.current = -1;
  }, []);

  const undo = useCallback(() => {
    if (historyIdxRef.current <= 0) return;
    historyIdxRef.current--;
    const snap = historyRef.current[historyIdxRef.current];
    restoringRef.current = true;
    setVideoClips(snap.vc); setAudioClips(snap.ac); setOverlayClips(snap.oc); setCaptionTracks(snap.ct || []);
    setTimeout(() => { restoringRef.current = false; }, 0);
    toast('Desfeito', { icon: '↶', duration: 1200 });
  }, []);

  const redo = useCallback(() => {
    if (historyIdxRef.current >= historyRef.current.length - 1) return;
    historyIdxRef.current++;
    const snap = historyRef.current[historyIdxRef.current];
    restoringRef.current = true;
    setVideoClips(snap.vc); setAudioClips(snap.ac); setOverlayClips(snap.oc); setCaptionTracks(snap.ct || []);
    setTimeout(() => { restoringRef.current = false; }, 0);
    toast('Refeito', { icon: '↷', duration: 1200 });
  }, []);

  // ---- State: selection ----
  const [selected, setSelected] = useState<Selection>(null);
  const [contextMenu, setContextMenu] = useState<{ x: number; y: number; kind: 'video' | 'audio' | 'overlay'; id: string } | null>(null);
  const [projectId, setProjectId] = useState<string | null>(null);
  const [projectName, setProjectName] = useState('Meu projeto');
  const [showProjects, setShowProjects] = useState(false);
  const [savedProjects, setSavedProjects] = useState<any[]>([]);
  const [savingProject, setSavingProject] = useState(false);
  // Auto-save: when projectId is set we save automatically on changes (debounced).
  const [lastSavedAt, setLastSavedAt] = useState<number | null>(null);
  const [autoSavingProject, setAutoSavingProject] = useState(false);
  // Tick every 30s so the "salvo há X" label refreshes without us having to invalidate state.
  const [savedAgoTick, setSavedAgoTick] = useState(0);
  useEffect(() => {
    const h = setInterval(() => setSavedAgoTick(t => t + 1), 30_000);
    return () => clearInterval(h);
  }, []);
  const [showCaptionsEditor, setShowCaptionsEditor] = useState(false);
  const [galleryPicker, setGalleryPicker] = useState<'video' | 'image' | null>(null);
  const [mediaRefreshKey, setMediaRefreshKey] = useState(0);
  const [showAIEdit, setShowAIEdit] = useState(false);
  // Clipboard for cut/copy/paste — holds a snapshot of any AnyClip + its kind
  const clipboardRef = useRef<{ kind: 'video' | 'audio' | 'overlay'; clip: AnyClip } | null>(null);

  // Confirm + Prompt dialogs (replaces ugly window.confirm/prompt)
  const [confirmDialog, setConfirmDialog] = useState<{
    title: string; body: string; danger?: boolean;
    confirmLabel?: string; onConfirm: () => void;
  } | null>(null);
  const [promptDialog, setPromptDialog] = useState<{
    title: string; body?: string; defaultValue?: string;
    placeholder?: string; onSubmit: (v: string) => void;
  } | null>(null);

  const askConfirm = (opts: { title: string; body: string; danger?: boolean; confirmLabel?: string; onConfirm: () => void }) =>
    setConfirmDialog(opts);
  const askPrompt = (opts: { title: string; body?: string; defaultValue?: string; placeholder?: string; onSubmit: (v: string) => void }) =>
    setPromptDialog(opts);
  // hasStarted = false → user lands on a selection screen (new project / open existing)
  // Auto-start when timeline already has clips on mount (e.g. user navigated back)
  const [hasStarted, setHasStarted] = useState(() => scenes.length > 0);
  const sel: AnyClip | null = useMemo(() => {
    if (!selected) return null;
    if (selected.kind === 'video') return videoClips.find(c => c.id === selected.id) || null;
    if (selected.kind === 'audio') return audioClips.find(c => c.id === selected.id) || null;
    return overlayClips.find(c => c.id === selected.id) || null;
  }, [selected, videoClips, audioClips, overlayClips]);

  // ---- State: zoom ----
  const [pxPerSec, setPxPerSec] = useState(50);
  const timelineWidth = Math.max(800, totalDuration * pxPerSec + 80);

  // ---- State: playback ----
  const [playing, setPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const playRequestRef = useRef<number | null>(null);
  const playStartRef = useRef<{ wallStart: number; timelineStart: number } | null>(null);

  const videoElRefs = useRef<Map<string, HTMLVideoElement>>(new Map());
  const audioElRefs = useRef<Map<string, HTMLAudioElement>>(new Map());

  // Stage size — used to scale text overlays so the preview matches the export
  // pixel-for-pixel. We track height because font_size is referenced to a
  // FONT_REFERENCE_HEIGHT-tall canvas (see helper above).
  const stageRef = useRef<HTMLDivElement>(null);
  const [stageHeight, setStageHeight] = useState(0);
  useEffect(() => {
    const el = stageRef.current;
    if (!el) return;
    const observer = new ResizeObserver(entries => {
      for (const entry of entries) setStageHeight(entry.contentRect.height);
    });
    observer.observe(el);
    setStageHeight(el.getBoundingClientRect().height);
    return () => observer.disconnect();
  }, []);

  // Único ponto de seek do editor. Durante a reprodução, rebase a âncora do
  // relógio — senão o rAF continuava contando a partir do ponto onde o play
  // começou e o playhead "voltava sozinho" logo depois de arrastar.
  const seekTo = useCallback((t: number | ((prev: number) => number)) => {
    setCurrentTime(prev => {
      const raw = typeof t === 'function' ? (t as (p: number) => number)(prev) : t;
      const clamped = Math.max(0, Math.min(liveRef.current.totalDuration, Number.isFinite(raw) ? raw : prev));
      if (playingRef.current && playStartRef.current) {
        playStartRef.current = { wallStart: performance.now(), timelineStart: clamped };
      }
      return clamped;
    });
  }, []);

  const stopPlayback = useCallback(() => {
    setPlaying(false);
    if (playRequestRef.current) cancelAnimationFrame(playRequestRef.current);
    videoElRefs.current.forEach(v => { try { v.pause(); } catch {} });
    audioElRefs.current.forEach(a => { try { a.pause(); } catch {} });
  }, []);

  // Add / remove clips
  const removeClip = useCallback((kind: 'video' | 'audio' | 'overlay', id: string) => {
    if (kind === 'video') setVideoClips(p => p.filter(c => c.id !== id));
    else if (kind === 'audio') setAudioClips(p => p.filter(c => c.id !== id));
    else setOverlayClips(p => p.filter(c => c.id !== id));
    setSelected(s => s?.id === id ? null : s);
  }, []);

  const updateClip = useCallback((kind: 'video' | 'audio' | 'overlay', id: string, patch: any) => {
    if (kind === 'video') setVideoClips(p => p.map(c => c.id === id ? { ...c, ...patch } : c));
    else if (kind === 'audio') setAudioClips(p => p.map(c => c.id === id ? { ...c, ...patch } : c));
    else setOverlayClips(p => p.map(c => c.id === id ? { ...c, ...patch } : c));
  }, []);

  // ---- Cut / Copy / Paste ----
  const copySelected = useCallback(() => {
    if (!selected || !sel) return;
    clipboardRef.current = { kind: selected.kind, clip: JSON.parse(JSON.stringify(sel)) };
    toast('Copiado', { icon: '📋', duration: 1000 });
  }, [selected, sel]);

  const cutSelected = useCallback(() => {
    if (!selected || !sel) return;
    clipboardRef.current = { kind: selected.kind, clip: JSON.parse(JSON.stringify(sel)) };
    removeClip(selected.kind, selected.id);
    toast('Recortado', { icon: '✂️', duration: 1000 });
  }, [selected, sel, removeClip]);

  const paste = useCallback(() => {
    const cb = clipboardRef.current;
    if (!cb) return;
    const newId_ = newId();
    const dur = (cb.clip as any).duration || 5;
    // Lê de refs: callback estável (currentTime nos deps re-registrava o
    // listener de teclado a cada tick do playhead)
    const start = currentTimeRef.current;
    const { videoClips, audioClips, overlayClips } = liveRef.current;
    const isVisualKind = cb.kind === 'video' || cb.kind === 'overlay';
    const lane = isVisualKind
      ? nextFreeLane([videoClips, overlayClips], start, start + dur)
      : nextFreeLane([audioClips], start, start + dur);
    const newClip: any = { ...cb.clip, id: newId_, start, lane };
    if (cb.kind === 'video') setVideoClips(p => [...p, newClip]);
    else if (cb.kind === 'audio') setAudioClips(p => [...p, newClip]);
    else setOverlayClips(p => [...p, newClip]);
    setSelected({ kind: cb.kind, id: newId_ });
    toast('Colado', { icon: '📌', duration: 1000 });
  }, []);

  // Keyboard shortcuts.
  // splitAtPlayhead é declarado mais abaixo no componente — a ref evita TDZ no
  // array de deps e mantém o handler sempre apontando pra versão mais recente.
  const splitRef = useRef<() => void>(() => {});
  // Mesmo motivo do splitRef: sem isto o Ctrl+S salvava com o nome de projeto
  // capturado no primeiro render (renomear + Ctrl+S gravava o nome antigo).
  const saveRef = useRef<(asTemplate?: boolean) => void>(() => {});
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement;
      const isInput = target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable;
      // Undo/Redo work even when not focused on inputs (most editors)
      if ((e.ctrlKey || e.metaKey) && !e.shiftKey && (e.key === 'z' || e.key === 'Z')) {
        e.preventDefault(); undo(); return;
      }
      if ((e.ctrlKey || e.metaKey) && (e.key === 'y' || e.key === 'Y' || (e.shiftKey && (e.key === 'z' || e.key === 'Z')))) {
        e.preventDefault(); redo(); return;
      }
      // Ctrl+S → save project
      if ((e.ctrlKey || e.metaKey) && (e.key === 's' || e.key === 'S')) {
        e.preventDefault(); saveRef.current(false); return;
      }
      // Cut / Copy / Paste — only when not focused on inputs
      if (!isInput && (e.ctrlKey || e.metaKey)) {
        if (e.key === 'c' || e.key === 'C') { e.preventDefault(); copySelected(); return; }
        if (e.key === 'x' || e.key === 'X') { e.preventDefault(); cutSelected(); return; }
        if (e.key === 'v' || e.key === 'V') { e.preventDefault(); paste(); return; }
      }
      if (isInput) return;
      if ((e.key === 'Delete' || e.key === 'Backspace') && selected) {
        e.preventDefault(); removeClip(selected.kind, selected.id);
      }
      // S → cortar clipe selecionado no playhead (o tooltip já prometia isso)
      if ((e.key === 's' || e.key === 'S') && !e.ctrlKey && !e.metaKey) {
        e.preventDefault(); splitRef.current(); return;
      }
      // M → mutar/desmutar o vídeo selecionado
      if ((e.key === 'm' || e.key === 'M') && selected?.kind === 'video') {
        e.preventDefault();
        const v = videoClips.find(c => c.id === selected.id);
        if (v) updateClip('video', v.id, { muted: !v.muted });
        return;
      }
      // ←/→ → andar 0,1s no playhead (Shift = 1s); Home/End → início/fim
      if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
        e.preventDefault();
        const step = (e.shiftKey ? 1 : 0.1) * (e.key === 'ArrowLeft' ? -1 : 1);
        seekTo(t => t + step);
        return;
      }
      if (e.key === 'Home') { e.preventDefault(); seekTo(0); return; }
      if (e.key === 'End') { e.preventDefault(); seekTo(totalDuration); return; }
      if (e.key === ' ' && target.tagName === 'BODY') {
        e.preventDefault(); setPlaying(p => !p);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected, removeClip, undo, redo, copySelected, cutSelected, paste, videoClips, updateClip, totalDuration]);

  // Auto-snapshot for undo: debounced, captures any state change
  useEffect(() => {
    if (restoringRef.current) return;
    const t = setTimeout(() => pushHistory(videoClips, audioClips, overlayClips, captionTracks), 350);
    return () => clearTimeout(t);
  }, [videoClips, audioClips, overlayClips, captionTracks, pushHistory]);

  // Sync playback.
  // Performance fix: o setCurrentTime ANTES era chamado a cada frame (60fps) → cada
  // chamada disparava re-render do componente inteiro + recálculo de activeOverlays
  // /activeCaptions (que dependem de currentTime). Com 5-20 clips o cost vira lag
  // visível. Agora: o sync REAL de <video>/<audio> usa o tempo exato (`t`) sem state;
  // o React só atualiza o currentTime visual a cada ~100ms (10fps) — suficiente pra
  // o playhead da timeline parecer fluido, mas 6× menos re-renders.
  const lastUiUpdateRef = useRef(0);
  // O loop de rAF é criado uma vez por play (deps: [playing]). Sem estes espelhos
  // ele congelava a lista de clipes do instante do play: mutar, mudar volume,
  // adicionar clipe ou esticar a timeline durante a reprodução não tinha efeito
  // algum (o tick reescrevia muted/volume com os valores antigos a cada frame).
  const liveRef = useRef({ videoClips, audioClips, overlayClips, totalDuration });
  useEffect(() => { liveRef.current = { videoClips, audioClips, overlayClips, totalDuration }; });
  const playingRef = useRef(false);
  useEffect(() => { playingRef.current = playing; }, [playing]);
  // Espelhos p/ callbacks estáveis: sem eles, cada tick de currentTime (10x/s
  // tocando) ou mousemove de drag recriava os handlers passados pra timeline
  // inteira e forçava re-render de TODOS os ClipBlocks/biblioteca/painéis.
  const currentTimeRef = useRef(0);
  useEffect(() => { currentTimeRef.current = currentTime; }, [currentTime]);
  const pxPerSecRef = useRef(pxPerSec);
  useEffect(() => { pxPerSecRef.current = pxPerSec; }, [pxPerSec]);

  useEffect(() => {
    if (!playing) return;
    playStartRef.current = { wallStart: performance.now(), timelineStart: currentTime };
    lastUiUpdateRef.current = 0;
    const tick = () => {
      const { wallStart, timelineStart } = playStartRef.current!;
      const { videoClips, audioClips, totalDuration } = liveRef.current;
      const now = performance.now();
      const t = timelineStart + (now - wallStart) / 1000;
      if (t >= totalDuration) { setCurrentTime(totalDuration); stopPlayback(); return; }

      // Throttle UI update pra ~10fps (100ms). Sync de <video>/<audio> abaixo continua
      // usando `t` real em cada frame, então o áudio/vídeo ficam smooth.
      if (now - lastUiUpdateRef.current >= 100) {
        lastUiUpdateRef.current = now;
        setCurrentTime(t);
      }

      videoClips.forEach(clip => {
        const ref = videoElRefs.current.get(clip.id); if (!ref) return;
        const inRange = t >= clip.start && t < clip.start + clip.duration;
        if (inRange) {
          const localT = (t - clip.start) + (clip.trim_start || 0);
          if (Math.abs(ref.currentTime - localT) > 0.3) ref.currentTime = localT;
          if (ref.paused) ref.play().catch(() => {});
          ref.muted = clip.muted; ref.volume = clip.volume ?? 1;
        } else if (!ref.paused) ref.pause();
      });
      audioClips.forEach(clip => {
        const ref = audioElRefs.current.get(clip.id); if (!ref) return;
        const inRange = t >= clip.start && t < clip.start + clip.duration;
        if (inRange) {
          const localT = (t - clip.start) + (clip.trim_start || 0);
          if (Math.abs(ref.currentTime - localT) > 0.3) ref.currentTime = localT;
          if (ref.paused) ref.play().catch(() => {});
          ref.volume = clip.volume ?? 1;
        } else if (!ref.paused) ref.pause();
      });

      playRequestRef.current = requestAnimationFrame(tick);
    };
    playRequestRef.current = requestAnimationFrame(tick);
    return () => { if (playRequestRef.current) cancelAnimationFrame(playRequestRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playing]);

  // Passive sync — when paused (scrubbing), keep <video>/<audio> elements aligned
  // with the timeline so the preview reflects the current playhead AND so that
  // when the user hits play next, the elements are already at the right frame
  // (avoids the seek+decode lag spike at play-start).
  useEffect(() => {
    if (playing) return; // play loop owns sync while playing
    videoClips.forEach(clip => {
      const ref = videoElRefs.current.get(clip.id); if (!ref) return;
      const inRange = currentTime >= clip.start && currentTime < clip.start + clip.duration;
      if (inRange) {
        const localT = (currentTime - clip.start) + (clip.trim_start || 0);
        if (Math.abs(ref.currentTime - localT) > 0.08) ref.currentTime = localT;
        if (!ref.paused) ref.pause();
      } else if (!ref.paused) {
        ref.pause();
      }
    });
    audioClips.forEach(clip => {
      const ref = audioElRefs.current.get(clip.id); if (!ref) return;
      const inRange = currentTime >= clip.start && currentTime < clip.start + clip.duration;
      if (inRange) {
        const localT = (currentTime - clip.start) + (clip.trim_start || 0);
        if (Math.abs(ref.currentTime - localT) > 0.08) ref.currentTime = localT;
        if (!ref.paused) ref.pause();
      } else if (!ref.paused) {
        ref.pause();
      }
    });
  }, [currentTime, playing, videoClips, audioClips]);

  // ---- Add clips ----
  // Routes any media asset to the correct track based on its mime_type.
  // useCallback + refs: prop estável pra MediaLibrary (React.memo) não re-renderizar
  // a cada tick do playhead.
  const addVideoFromLibrary = useCallback((asset: GeneratedVideo) => {
    if (!asset.original_url) { toast.error(t('videoStudio.gallery.notReady')); return; }
    const mime = asset.mime_type || '';
    const at = currentTimeRef.current;
    const { videoClips, audioClips, overlayClips } = liveRef.current;

    if (mime.startsWith('audio/')) {
      const dur = asset.duration_seconds || 10;
      const lane = nextFreeLane([audioClips], at, at + dur);
      const id = newId();
      setAudioClips(prev => [...prev, {
        id, kind: 'audio', url: asset.original_url!,
        name: asset.title || 'Áudio', start: at, duration: dur,
        volume: 1, lane
      }]);
      setSelected({ kind: 'audio', id });
      toast.success('Áudio adicionado à timeline');
      return;
    }

    if (mime.startsWith('image/')) {
      const dur = 3;
      const lane = nextFreeLane([videoClips, overlayClips], at, at + dur);
      const id = newId();
      setOverlayClips(prev => [...prev, {
        id, kind: 'image', url: asset.original_url!,
        start: at, duration: dur,
        width_pct: 30, position_x_pct: 50, position_y_pct: 50, opacity: 1, lane
      } as ImageOverlayClip]);
      setSelected({ kind: 'overlay', id });
      toast.success('Imagem adicionada — ajuste no painel direito');
      return;
    }

    // Default: video
    const dur = asset.duration_seconds || 5;
    const lane = nextFreeLane([videoClips, overlayClips], at, at + dur);
    const id = newId();
    setVideoClips(prev => [...prev, {
      id, kind: 'video',
      source_video_id: asset.id, url: asset.original_url!,
      thumbnail_url: asset.thumbnail_url || undefined,
      start: at, duration: dur,
      muted: false, volume: 1, filter: 'none', lane
    }]);
    setSelected({ kind: 'video', id });
    toast.success(t('videoStudio.gallery.addedToTimeline'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Ponte pra página adicionar mídia sem remontar o editor (ver TimelineEditorHandle)
  useImperativeHandle(ref, () => ({
    addAsset: (asset: GeneratedVideo) => {
      setHasStarted(true);
      addVideoFromLibrary(asset);
    }
  }));

  const audioFileRef = useRef<HTMLInputElement>(null);
  const imageFileRef = useRef<HTMLInputElement>(null);

  const handleAudioUpload = async (file: File) => {
    try {
      const r = await videoStudioService.uploadAudio(file);
      const duration = await new Promise<number>((resolve) => {
        const a = document.createElement('audio');
        a.preload = 'metadata';
        a.onloadedmetadata = () => resolve(a.duration || 30);
        a.onerror = () => resolve(30);
        a.src = URL.createObjectURL(file);
      });
      const lane = nextFreeLane([audioClips], currentTime, currentTime + duration);
      setAudioClips(prev => [...prev, {
        id: newId(), kind: 'audio', url: r.url, name: file.name,
        start: currentTime, duration, volume: 1, lane
      }]);
      toast.success('Áudio adicionado');
    } catch (e: any) {
      toast.error(e.response?.data?.message || 'Falha no upload de áudio');
    }
  };

  const handleImageUpload = async (file: File) => {
    try {
      const r = await videoStudioService.uploadImage(file);
      const id = newId();
      // Image goes ABOVE existing video/overlay clips on the same track — find next free lane
      const lane = nextFreeLane([videoClips, overlayClips], currentTime, currentTime + 3);
      setOverlayClips(prev => [...prev, {
        id, kind: 'image', url: r.url,
        start: currentTime, duration: 3,
        width_pct: 30, position_x_pct: 50, position_y_pct: 50, opacity: 1, lane
      } as ImageOverlayClip]);
      setSelected({ kind: 'overlay', id });
      toast.success('Imagem adicionada — ajuste no painel direito');
    } catch (e: any) {
      toast.error(e.response?.data?.message || 'Falha no upload');
    }
  };

  const addText = () => {
    const id = newId();
    const lane = nextFreeLane([videoClips, overlayClips], currentTime, currentTime + 3);
    setOverlayClips(prev => [...prev, {
      id, kind: 'text', text: 'Digite seu texto',
      start: currentTime, duration: 3,
      position: 'bottom', position_x_pct: 50, position_y_pct: 88,
      color: '#FFFFFF', background: 'rgba(0,0,0,0.5)',
      font_size: 56, font_family: 'Inter', lane
    } as TextOverlayClip]);
    setSelected({ kind: 'overlay', id });
    toast.success(t('videoStudio.editor.textAdded'));
  };

  // Default caption styling — applies to all cues in a track. User can edit later.
  const DEFAULT_CAPTION_STYLE: CaptionTrack['style'] = {
    font_size: 42, font_family: 'Inter', bold: true,
    color: '#FFFFFF', background: 'rgba(0,0,0,0.75)', position: 'bottom'
  };

  // Auto-generate ONE caption track covering the WHOLE timeline.
  // We transcribe every video clip (and every audio clip) separately, then
  // merge all segments into a single track with cues offset by each clip's
  // position on the timeline.
  const [transcribing, setTranscribing] = useState(false);
  const autoTranscribe = async () => {
    // Cada CLIPE da timeline que tem áudio. trim_start importa: um clipe cortado
    // reproduz o trecho [trim_start, trim_start+duration) do arquivo, e é esse
    // trecho que precisa virar legenda (antes o código pegava sempre o começo do
    // arquivo, então a segunda metade de um corte recebia as falas da primeira).
    const clips: { url: string; videoId?: string; clipStart: number; clipDuration: number; trimStart: number; label: string }[] = [];
    [...videoClips]
      .filter(v => !!v.url && !v.muted)
      .sort((a, b) => a.start - b.start)
      .forEach((v, i) => {
        clips.push({ url: v.url, videoId: v.source_video_id, clipStart: v.start, clipDuration: v.duration, trimStart: v.trim_start || 0, label: `vídeo #${i + 1}` });
      });
    [...audioClips]
      .filter(a => !!a.url)
      .sort((a, b) => a.start - b.start)
      .forEach((a, i) => {
        clips.push({ url: a.url, clipStart: a.start, clipDuration: a.duration, trimStart: a.trim_start || 0, label: `áudio #${i + 1}` });
      });

    if (clips.length === 0) {
      toast.error('Adicione um vídeo (não-mudo) ou áudio na timeline primeiro');
      return;
    }

    // O backend transcreve o ARQUIVO inteiro por chamada: transcrever o mesmo
    // arquivo uma vez por pedaço cobrava o dobro/triplo pelo mesmo áudio.
    const uniqueSources = new Map<string, { url: string; videoId?: string; label: string; span: number }>();
    for (const c of clips) {
      const prev = uniqueSources.get(c.url);
      const span = c.trimStart + c.clipDuration;
      if (!prev) uniqueSources.set(c.url, { url: c.url, videoId: c.videoId, label: c.label, span });
      else prev.span = Math.max(prev.span, span);
    }
    const sources = Array.from(uniqueSources.values());

    const totalDuration = clips.reduce((acc, s) => Math.max(acc, s.clipStart + s.clipDuration), 0);
    const minutesEstimate = sources.reduce((acc, s) => acc + s.span, 0) / 60;
    const creditsEstimate = Math.max(1, Math.ceil(minutesEstimate * 2));

    const runTranscribe = async () => {
      setTranscribing(true);
      toast.loading(`Transcrevendo ${sources.length} faixa(s)…`, { id: 'transcribe' });
      try {
        const allCues: CaptionCue[] = [];
        let detectedLang: string | null = null;
        let totalCharged = 0;
        let failures = 0;

        for (let i = 0; i < sources.length; i++) {
          const src = sources[i];
          toast.loading(`Transcrevendo ${src.label} (${i + 1}/${sources.length})…`, { id: 'transcribe' });
          try {
            const r = await videoStudioService.transcribe({
              source_url: src.url,
              video_id: src.videoId,
              language: 'pt'
            });
            if (!r.success || !r.data) { failures++; continue; }
            const data = r.data;
            if (!detectedLang && data.language) detectedLang = data.language;
            totalCharged += data.credits_charged || 0;

            // Espalha os segmentos deste ARQUIVO em todos os clipes que o usam,
            // convertendo tempo-do-arquivo → tempo-da-timeline e descartando o
            // que cai fora da janela aparada do clipe.
            for (const clip of clips.filter(c => c.url === src.url)) {
              data.segments.forEach(seg => {
                const localStart = Math.max(0, Math.min(seg.start - clip.trimStart, clip.clipDuration));
                const localEnd = Math.max(0, Math.min(seg.end - clip.trimStart, clip.clipDuration));
                if (localEnd <= localStart) return;
                allCues.push({
                  id: newId(),
                  start: clip.clipStart + localStart,
                  end: clip.clipStart + localEnd,
                  text: seg.text
                });
              });
            }
          } catch (innerErr: any) {
            failures++;
            console.warn(`Transcribe failed for ${src.label}:`, innerErr);
          }
        }

        toast.dismiss('transcribe');
        if (allCues.length === 0) {
          toast.error('Nenhum áudio reconhecível foi detectado');
          return;
        }

        // Sort cues by start time and merge into ONE caption track spanning the whole timeline.
        allCues.sort((a, b) => a.start - b.start);
        const lang = detectedLang || 'pt';
        const lane = nextFreeLane([videoClips, overlayClips, captionTracks], 0, totalDuration);
        setCaptionTracks(prev => [...prev, {
          id: newId(), kind: 'caption-track',
          start: 0, duration: totalDuration, lane,
          language: lang,
          name: `Legendas ${lang.toUpperCase()}`,
          style: { ...DEFAULT_CAPTION_STYLE },
          cues: allCues
        }]);

        const failNote = failures > 0 ? ` (${failures} faixa(s) falharam)` : '';
        toast.success(`Bloco de legendas criado: ${allCues.length} cues, ${totalCharged} créditos${failNote}`);
      } catch (e: any) {
        toast.dismiss('transcribe');
        const code = e.response?.data?.code;
        if (code === 'INSUFFICIENT_VIDEO_CREDITS') toast.error('Créditos insuficientes');
        else toast.error(e.response?.data?.message || e.message || 'Falha na transcrição');
      } finally {
        setTranscribing(false);
      }
    };

    askConfirm({
      title: 'Gerar legendas do áudio',
      body: `Vamos transcrever o áudio de TODAS as ${sources.length} faixa(s) da timeline em UM bloco único de legendas. Você edita o estilo depois no editor.\n\nCusto estimado: ~${creditsEstimate} créditos.`,
      confirmLabel: 'Transcrever',
      onConfirm: runTranscribe
    });
  };

  // Manual: create one caption track with one starter cue.
  // For multi-language, just call again — each call creates a new track.
  const addCaption = (language?: string) => {
    const id = newId();
    const dur = videoClips.length > 0
      ? videoClips.reduce((a, c) => Math.max(a, c.start + c.duration), 0)
      : 5;
    const lane = nextFreeLane([videoClips, overlayClips, captionTracks], 0, dur);
    setCaptionTracks(prev => [...prev, {
      id, kind: 'caption-track',
      start: 0, duration: dur, lane,
      language: language || 'pt',
      name: language ? `Legendas ${language.toUpperCase()}` : 'Legendas',
      style: { ...DEFAULT_CAPTION_STYLE },
      cues: [{ id: newId(), start: 0, end: Math.min(3, dur), text: 'Sua legenda aqui' }]
    }]);
    setSelected({ kind: 'overlay', id });
    toast.success('Bloco de legendas adicionado');
  };

  // CRUD for caption tracks + cues
  const updateCaptionTrack = (id: string, patch: Partial<CaptionTrack>) =>
    setCaptionTracks(p => p.map(t => t.id === id ? { ...t, ...patch } : t));
  const removeCaptionTrack = (id: string) => {
    setCaptionTracks(p => p.filter(t => t.id !== id));
    if (selected?.id === id) setSelected(null);
  };
  const updateCue = (trackId: string, cueId: string, patch: Partial<CaptionCue>) =>
    setCaptionTracks(p => p.map(t => {
      if (t.id !== trackId) return t;
      const cues = t.cues.map(c => c.id === cueId ? { ...c, ...patch } : c);
      return { ...t, cues, duration: Math.max(t.duration, ...cues.map(c => c.end)) };
    }));
  const addCueToTrack = (trackId: string, atTime: number) =>
    setCaptionTracks(p => p.map(t => {
      if (t.id !== trackId) return t;
      const localStart = Math.max(0, atTime - t.start);
      const newCue: CaptionCue = { id: newId(), start: localStart, end: localStart + 2, text: 'Nova legenda' };
      return { ...t, cues: [...t.cues, newCue], duration: Math.max(t.duration, newCue.end) };
    }));
  const removeCueFromTrack = (trackId: string, cueId: string) =>
    setCaptionTracks(p => p.map(t => t.id !== trackId ? t : ({ ...t, cues: t.cues.filter(c => c.id !== cueId) })));

  // SRT parser/generator
  const parseSRT = (srt: string): CaptionCue[] => {
    const blocks = srt.replace(/\r\n/g, '\n').trim().split(/\n\n+/);
    const cues: CaptionCue[] = [];
    for (const block of blocks) {
      const lines = block.split('\n').filter(Boolean);
      if (lines.length < 2) continue;
      const timeLine = lines[0].includes('-->') ? lines[0] : lines[1];
      const textLines = lines[0].includes('-->') ? lines.slice(1) : lines.slice(2);
      const m = timeLine.match(/(\d+):(\d+):(\d+)[,.](\d+)\s*-->\s*(\d+):(\d+):(\d+)[,.](\d+)/);
      if (!m) continue;
      const toSec = (h: string, mn: string, s: string, ms: string) =>
        Number(h) * 3600 + Number(mn) * 60 + Number(s) + Number(ms) / 1000;
      cues.push({
        id: newId(),
        start: toSec(m[1], m[2], m[3], m[4]),
        end: toSec(m[5], m[6], m[7], m[8]),
        text: textLines.join('\n').trim()
      });
    }
    return cues;
  };
  const cuesToSRT = (cues: CaptionCue[]): string => {
    const fmt = (s: number) => {
      const h = Math.floor(s / 3600);
      const m = Math.floor((s % 3600) / 60);
      const sec = Math.floor(s % 60);
      const ms = Math.round((s - Math.floor(s)) * 1000);
      return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')},${String(ms).padStart(3, '0')}`;
    };
    return cues.slice().sort((a, b) => a.start - b.start)
      .map((c, i) => `${i + 1}\n${fmt(c.start)} --> ${fmt(c.end)}\n${c.text}\n`).join('\n');
  };
  // Import SRT/VTT → adds a NEW track (per spec C2 — "acrescenta")
  const importSRT = async (file: File) => {
    try {
      const text = await file.text();
      const cues = parseSRT(text);
      if (cues.length === 0) { toast.error('Nenhuma legenda válida no arquivo'); return; }
      const id = newId();
      const duration = Math.max(...cues.map(c => c.end));
      const lane = nextFreeLane([videoClips, overlayClips, captionTracks], 0, duration);
      setCaptionTracks(prev => [...prev, {
        id, kind: 'caption-track',
        start: 0, duration, lane,
        language: 'imported',
        name: file.name.replace(/\.(srt|vtt)$/i, ''),
        style: { ...DEFAULT_CAPTION_STYLE },
        cues
      }]);
      toast.success(`${cues.length} legendas importadas`);
    } catch (e: any) {
      toast.error('Falha ao ler o arquivo SRT');
    }
  };
  const exportSRT = (trackId: string) => {
    const track = captionTracks.find(t => t.id === trackId);
    if (!track) return;
    const blob = new Blob([cuesToSRT(track.cues)], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `${track.name || 'legendas'}.srt`;
    a.click(); URL.revokeObjectURL(url);
  };

  // Detach audio from a video clip — creates an independent AudioClip and mutes the source
  const detachAudio = (videoId: string) => {
    const v = videoClips.find(c => c.id === videoId);
    if (!v) return;
    if (audioClips.some(a => a.detached_from === v.id)) {
      toast.error('Esse vídeo já teve o áudio separado');
      return;
    }
    const newAudioId = newId();
    setAudioClips(prev => [...prev, {
      id: newAudioId, kind: 'audio',
      url: v.url, name: 'Áudio do vídeo',
      start: v.start, duration: v.duration,
      trim_start: v.trim_start, volume: 1,
      detached_from: v.id,
      lane: nextFreeLane([audioClips], v.start, v.start + v.duration)
    }]);
    updateClip('video', v.id, { muted: true });
    toast.success('Áudio separado pro track de áudio');
  };

  // ---- Auto-save: silent debounced save once the project has an ID. ----
  // We track a "dirty since loaded" flag via lastSavedAt vs an internal change tick
  // (deps below). The first hydration of state from a load shouldn't trigger a save —
  // loadProject sets lastSavedAt to now() right after hydrating, so the next render's
  // change-detector sees no diff. After that, any actual user edit fires the timer.
  const autoSaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Ref espelha o projectId atual. O closure do setTimeout compara o ID capturado
  // com este ref pra abortar caso o user tenha trocado de projeto durante o debounce.
  // Sem isso, há uma race onde o save antigo (com ID stale) sobrescreveria o projeto
  // ATUAL com dados ERRADOS — exatamente o bug reportado.
  const currentProjectIdRef = useRef<string | null>(null);
  useEffect(() => { currentProjectIdRef.current = projectId; }, [projectId]);

  useEffect(() => {
    if (!projectId) return;             // never auto-save unsaved/unnamed projects
    if (savingProject) return;          // don't pile up while a save is in flight
    if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    // Captura o ID do projeto NESTE momento (closure). Se mudar antes do timer
    // disparar (user trocou de projeto), o save é abortado.
    const targetProjectId = projectId;
    autoSaveTimerRef.current = setTimeout(async () => {
      // Aborta se o projeto atual já não é mais o que tava sendo editado
      if (currentProjectIdRef.current !== targetProjectId) return;
      if (savingProject) return;
      setAutoSavingProject(true);
      try {
        const payload = {
          id: targetProjectId,
          name: projectName.trim() || `Projeto ${new Date().toLocaleString('pt-BR')}`,
          aspect_ratio: aspectRatio,
          duration_seconds: totalDuration,
          is_template: false,
          thumbnail_url: videoClips[0]?.thumbnail_url || undefined,
          data: { videoClips, audioClips, overlayClips, captionTracks, aspectRatio, pxPerSec }
        };
        // Double-check antes de disparar a request HTTP — proteção extra contra
        // troca de projeto durante o setAutoSavingProject acima.
        if (currentProjectIdRef.current !== targetProjectId) return;
        const r = await videoStudioService.saveProject(payload);
        if (r.success && r.data) setLastSavedAt(Date.now());
      } catch { /* silent — user will see the "salvo há X" stop updating */ }
      finally { setAutoSavingProject(false); }
    }, 3000);
    return () => {
      if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoClips, audioClips, overlayClips, captionTracks, aspectRatio, pxPerSec, projectName, projectId]);

  // ---- Save / Load project ----
  const saveProject = async (asTemplate = false, customName?: string) => {
    if (videoClips.length === 0 && audioClips.length === 0 && overlayClips.length === 0) {
      toast.error('Adicione pelo menos um clipe pra salvar');
      return;
    }
    // If no project name yet (new project, never saved) and no customName provided → ask via pretty dialog
    if (!projectId && !customName) {
      askPrompt({
        title: 'Salvar projeto',
        body: 'Dê um nome ao projeto pra encontrá-lo depois.',
        defaultValue: projectName || `Projeto ${new Date().toLocaleString('pt-BR')}`,
        placeholder: 'Nome do projeto',
        onSubmit: (name) => {
          setProjectName(name);
          // Re-call save with the chosen name
          setTimeout(() => saveProject(asTemplate, name), 0);
        }
      });
      return;
    }
    setSavingProject(true);
    try {
      const payload = {
        // "Salvar como modelo" sempre cria um registro NOVO. Mandando o id do
        // projeto aberto, ele próprio virava template — e o auto-save seguinte
        // (is_template: false) desfazia isso 3s depois.
        id: asTemplate ? undefined : (projectId || undefined),
        name: (customName || projectName).trim() || `Projeto ${new Date().toLocaleString('pt-BR')}`,
        aspect_ratio: aspectRatio,
        duration_seconds: totalDuration,
        is_template: asTemplate,
        thumbnail_url: videoClips[0]?.thumbnail_url || undefined,
        data: { videoClips, audioClips, overlayClips, captionTracks, aspectRatio, pxPerSec }
      };
      const r = await videoStudioService.saveProject(payload);
      if (r.success && r.data) {
        // Ao salvar um modelo seguimos editando o projeto original
        if (!asTemplate) setProjectId(r.data.id);
        setLastSavedAt(Date.now());
        toast.success(asTemplate ? 'Modelo salvo' : 'Projeto salvo');
      }
    } catch (e: any) {
      toast.error(e.response?.data?.message || 'Falha ao salvar');
    } finally {
      setSavingProject(false);
    }
  };

  saveRef.current = saveProject;

  const openProjectsList = async () => {
    setShowProjects(true);
    try {
      const r = await videoStudioService.listProjects();
      if (r.success && r.data) setSavedProjects(r.data);
    } catch { /* silent */ }
  };

  const loadProject = async (id: string) => {
    try {
      const r = await videoStudioService.getProject(id);
      if (!r.success || !r.data) return;
      const d = r.data.data || {};
      setVideoClips(d.videoClips || []);
      setAudioClips(d.audioClips || []);
      setOverlayClips(d.overlayClips || []);
      setCaptionTracks(d.captionTracks || []);
      // UI state: zoom & timeline ratio. Restored so reopening a project feels identical.
      if (typeof d.pxPerSec === 'number') setPxPerSec(d.pxPerSec);
      const loadedRatio = d.aspectRatio || r.data.aspect_ratio;
      if (loadedRatio && loadedRatio !== aspectRatio && onAspectRatioChange) {
        onAspectRatioChange(loadedRatio);
      }
      setProjectId(r.data.id);
      setProjectName(r.data.name);
      setShowProjects(false);
      setCurrentTime(0);
      setHasStarted(true);
      setSelected(null);
      resetHistory();
      // Treat the load itself as the last "save" — auto-save will only fire on subsequent edits.
      setLastSavedAt(Date.now());
      toast.success(`Projeto "${r.data.name}" carregado`);
    } catch (e: any) {
      toast.error('Falha ao carregar projeto');
    }
  };

  const removeProject = async (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    askConfirm({
      title: 'Excluir projeto?',
      body: 'O projeto será removido permanentemente. Esta ação não pode ser desfeita.',
      danger: true, confirmLabel: 'Excluir',
      onConfirm: async () => {
        try {
          await videoStudioService.deleteProject(id);
          setSavedProjects(prev => prev.filter(p => p.id !== id));
          if (projectId === id) setProjectId(null);
          toast.success('Projeto excluído');
        } catch { toast.error('Falha ao excluir'); }
      }
    });
  };

  // Duplicate a clip
  const duplicateClip = (kind: 'video' | 'audio' | 'overlay', id: string) => {
    const list: any[] = kind === 'video' ? videoClips : kind === 'audio' ? audioClips : overlayClips;
    const orig = list.find(c => c.id === id);
    if (!orig) return;
    // A cópia cai logo depois do original: se já houver clipe ali na mesma
    // camada, sobe de camada em vez de sobrepor (arrastar nunca deixa sobrepor,
    // duplicar deixava — e aí preview e export desenhavam os dois juntos).
    const start = orig.start + orig.duration;
    const lane = kind === 'audio'
      ? nextFreeLane([audioClips], start, start + orig.duration)
      : nextFreeLane([videoClips, overlayClips], start, start + orig.duration);
    const newClip = { ...orig, id: newId(), start, lane };
    if (kind === 'video') setVideoClips(p => [...p, newClip]);
    else if (kind === 'audio') setAudioClips(p => [...p, newClip]);
    else setOverlayClips(p => [...p, newClip]);
    setSelected({ kind, id: newClip.id });
  };

  // Split selected clip at playhead (S). Funciona pra vídeo, áudio E overlays
  // (texto/imagem — antes o botão aparecia mas não fazia nada com eles).
  // Mantém a metade esquerda selecionada pra emendar edições sem re-clicar.
  const splitAtPlayhead = useCallback(() => {
    if (!selected || !sel) {
      toast('Selecione um clipe para cortar', { icon: '✂️', duration: 2000 });
      return;
    }
    const splitPos = currentTime - (sel as any).start;
    // Silêncio aqui fazia a tesoura/atalho S parecerem quebrados
    if (splitPos <= MIN_CLIP_DURATION || splitPos >= (sel as any).duration - MIN_CLIP_DURATION) {
      toast('Posicione o playhead dentro do clipe (longe das bordas) para cortar', { icon: '✂️', duration: 2500 });
      return;
    }

    const leftId = newId();
    if (selected.kind === 'video') {
      const v = sel as VideoClip;
      const left: VideoClip = { ...v, id: leftId, duration: splitPos };
      const right: VideoClip = { ...v, id: newId(),
        start: v.start + splitPos, duration: v.duration - splitPos,
        trim_start: (v.trim_start || 0) + splitPos };
      setVideoClips(p => p.filter(c => c.id !== v.id).concat([left, right]));
    } else if (selected.kind === 'audio') {
      const a = sel as AudioClip;
      const left: AudioClip = { ...a, id: leftId, duration: splitPos };
      const right: AudioClip = { ...a, id: newId(),
        start: a.start + splitPos, duration: a.duration - splitPos,
        trim_start: (a.trim_start || 0) + splitPos };
      setAudioClips(p => p.filter(c => c.id !== a.id).concat([left, right]));
    } else {
      // Overlays (texto/imagem) são estáticos — não há trim, só divide a janela de exibição
      const o = sel as TextOverlayClip | ImageOverlayClip;
      const left = { ...o, id: leftId, duration: splitPos };
      const right = { ...o, id: newId(), start: o.start + splitPos, duration: o.duration - splitPos };
      setOverlayClips(p => p.filter(c => c.id !== o.id).concat([left, right] as any));
    }
    setSelected({ kind: selected.kind, id: leftId });
    toast('Clipe cortado', { icon: '✂️', duration: 1000 });
  }, [selected, sel, currentTime]);
  splitRef.current = splitAtPlayhead;

  // ---- Snap points ----
  const allSnapPoints = useMemo(() => {
    const pts = new Set<number>([0, currentTime]);
    [...videoClips, ...audioClips, ...overlayClips].forEach(c => {
      pts.add(c.start); pts.add(c.start + c.duration);
    });
    return Array.from(pts);
  }, [videoClips, audioClips, overlayClips, currentTime]);

  // Returns the snapped time AND whether it actually snapped (for visual feedback)
  const snap = useCallback((sec: number, exclude?: number): { value: number; snapped: number | null } => {
    const threshold = SNAP_THRESHOLD_PX / pxPerSec;
    let best = sec; let bestDist = Infinity; let snappedAt: number | null = null;
    for (const p of allSnapPoints) {
      if (exclude !== undefined && Math.abs(p - exclude) < 0.001) continue;
      const d = Math.abs(p - sec);
      if (d < threshold && d < bestDist) { best = p; bestDist = d; snappedAt = p; }
    }
    return { value: best, snapped: snappedAt };
  }, [allSnapPoints, pxPerSec]);

  const [snapLine, setSnapLine] = useState<number | null>(null);
  // Espelho do snap p/ onDragMove estável (snap depende de currentTime via
  // allSnapPoints — sem o ref, cada tick recriava toda a cadeia de drag)
  const snapRef = useRef(snap);
  useEffect(() => { snapRef.current = snap; }, [snap]);

  // ---- Drag/Resize (horizontal + vertical lane swap) ----
  type DragMode = 'move' | 'resize-left' | 'resize-right';
  const dragRef = useRef<{
    mode: DragMode; kind: 'video' | 'audio' | 'overlay'; id: string;
    startX: number; startY: number;
    orig: { start: number; duration: number; trim_start?: number; lane?: number };
    moved: boolean;
  } | null>(null);
  const [draggingId, setDraggingId] = useState<string | null>(null);

  const onDragMove = useCallback((e: MouseEvent) => {
    const d = dragRef.current; if (!d) return;
    // Lê tudo de refs: handler estável, não recriado por tick/zoom/estado
    const pxPerSec = pxPerSecRef.current;
    const snap = snapRef.current;
    const { videoClips, audioClips, overlayClips } = liveRef.current;
    const dxSec = (e.clientX - d.startX) / pxPerSec;
    const dyPx = e.clientY - d.startY;
    // Anything past a few pixels of drift counts as a real drag — keeps stray
    // sub-pixel jiggles on a click from triggering the post-drop collision pass
    // (which would move the block out from under the click and cause the click
    // event to fall through onto the timeline background, deselecting it).
    if (Math.abs(e.clientX - d.startX) > 3 || Math.abs(dyPx) > 3) d.moved = true;

    // Helper: collect every clip that shares this clip's track (visual track is shared
    // between videos and overlays; audio is its own pool). Excludes the clip being dragged.
    const isVisual = d.kind === 'video' || d.kind === 'overlay';
    const pool = (isVisual
      ? [...videoClips, ...overlayClips]
      : audioClips
    ).filter(c => c.id !== d.id);

    // Returns the first lane (>= preferLane) where [start..end] does not overlap any clip in pool
    const firstFreeLaneFrom = (preferLane: number, start: number, end: number): number => {
      for (let lane = preferLane; lane < 9999; lane++) {
        const overlaps = pool.some(c =>
          (c.lane || 0) === lane && c.start < end && c.start + c.duration > start
        );
        if (!overlaps) return lane;
      }
      return preferLane;
    };

    if (d.mode === 'move') {
      // Snap start, fall back to snapping end (so clip end can stick to another clip's start)
      const targetStart = d.orig.start + dxSec;
      const targetEnd = targetStart + d.orig.duration;
      const startSnap = snap(targetStart, d.orig.start);
      let newStart = startSnap.value;
      let snappedAt: number | null = startSnap.snapped;
      if (snappedAt === null) {
        const endSnap = snap(targetEnd, d.orig.start + d.orig.duration);
        if (endSnap.snapped !== null) {
          newStart = endSnap.value - d.orig.duration;
          snappedAt = endSnap.snapped;
        }
      }
      newStart = Math.max(0, newStart);

      // Vertical drag → desired lane based on where the mouse is
      const laneTentative = Math.max(0, (d.orig.lane || 0) + Math.round(dyPx / TRACK_HEIGHT));

      // CRITICAL: never overlap. If laneTentative is occupied at the new time range,
      // bump up to the next free lane until we find one.
      const resolvedLane = firstFreeLaneFrom(laneTentative, newStart, newStart + d.orig.duration);

      updateClip(d.kind, d.id, { start: newStart, lane: resolvedLane });
      setSnapLine(snappedAt);
    } else if (d.mode === 'resize-right') {
      const r = snap(d.orig.start + d.orig.duration + dxSec);
      let newDur = Math.max(MIN_CLIP_DURATION, r.value - d.orig.start);

      // Cap the new end so it never crosses into a neighbor on the same lane
      const lane = (d.orig.lane || 0);
      const neighborStart = pool
        .filter(c => (c.lane || 0) === lane && c.start >= d.orig.start)
        .reduce((min, c) => Math.min(min, c.start), Infinity);
      if (Number.isFinite(neighborStart)) {
        newDur = Math.min(newDur, neighborStart - d.orig.start);
      }
      newDur = Math.max(MIN_CLIP_DURATION, newDur);
      updateClip(d.kind, d.id, { duration: newDur });
      setSnapLine(r.snapped);
    } else if (d.mode === 'resize-left') {
      const r = snap(d.orig.start + dxSec);
      let newStart = Math.max(0, Math.min(d.orig.start + d.orig.duration - MIN_CLIP_DURATION, r.value));

      // Cap the new start so it never crosses backward into a neighbor's end on the same lane
      const lane = (d.orig.lane || 0);
      const origEnd = d.orig.start + d.orig.duration;
      const neighborEnd = pool
        .filter(c => (c.lane || 0) === lane && c.start + c.duration <= d.orig.start)
        .reduce((max, c) => Math.max(max, c.start + c.duration), -Infinity);
      if (Number.isFinite(neighborEnd)) {
        newStart = Math.max(newStart, neighborEnd);
      }
      newStart = Math.min(newStart, origEnd - MIN_CLIP_DURATION);

      const delta = newStart - d.orig.start;
      updateClip(d.kind, d.id, {
        start: newStart, duration: d.orig.duration - delta,
        trim_start: Math.max(0, (d.orig.trim_start || 0) + delta)
      });
      setSnapLine(r.snapped);
    }
  }, [updateClip]);

  const onDragEnd = useCallback(() => {
    const d = dragRef.current;
    dragRef.current = null;
    setDraggingId(null);
    setSnapLine(null);
    window.removeEventListener('mousemove', onDragMove);
    window.removeEventListener('mouseup', onDragEnd);

    // After drop: if the clip lands overlapping another in the same lane, push
    // to next free lane. Skip when the user just clicked (no actual drag) — the
    // clip didn't move, so re-laning it would only cause the click event that
    // fires after mouseup to miss the now-relocated block and deselect.
    if (d && d.mode === 'move' && d.moved) {
      const { videoClips, audioClips, overlayClips } = liveRef.current;
      // Use functional updaters to get the freshest state
      const resolveCollision = (
        clipsList: any[],
        otherList: any[],
        setter: (updater: any) => void,
        unified: boolean
      ) => {
        const me = clipsList.find(c => c.id === d.id);
        if (!me) return;
        const pool = unified ? [...clipsList, ...otherList] : [...clipsList];
        const collides = pool.some(c =>
          c.id !== me.id && (c.lane || 0) === (me.lane || 0) &&
          c.start < me.start + me.duration && c.start + c.duration > me.start
        );
        if (collides) {
          const newLane = nextFreeLane(unified ? [clipsList, otherList] : [clipsList], me.start, me.start + me.duration, me.id);
          setter((prev: any[]) => prev.map(c => c.id === me.id ? { ...c, lane: newLane } : c));
        }
      };
      if (d.kind === 'video')   resolveCollision(videoClips, overlayClips, setVideoClips, true);
      if (d.kind === 'overlay') resolveCollision(overlayClips, videoClips, setOverlayClips, true);
      if (d.kind === 'audio')   resolveCollision(audioClips, [], setAudioClips, false);
    }
  }, [onDragMove]);

  const startDrag = useCallback((
    e: React.MouseEvent, mode: DragMode,
    kind: 'video' | 'audio' | 'overlay', clip: { id: string; start: number; duration: number; trim_start?: number; lane?: number }
  ) => {
    e.preventDefault(); e.stopPropagation();
    setSelected({ kind, id: clip.id });
    setDraggingId(clip.id);
    dragRef.current = {
      mode, kind, id: clip.id, startX: e.clientX, startY: e.clientY,
      orig: { start: clip.start, duration: clip.duration, trim_start: clip.trim_start, lane: clip.lane },
      moved: false
    };
    window.addEventListener('mousemove', onDragMove);
    window.addEventListener('mouseup', onDragEnd);
  }, [onDragMove, onDragEnd]);

  const activeOverlays = useMemo(
    () => overlayClips.filter(c => currentTime >= c.start && currentTime < c.start + c.duration),
    [overlayClips, currentTime]
  );

  // For each caption track active at the playhead, find the cue currently visible
  const activeCaptions = useMemo(() => {
    const out: { trackId: string; track: CaptionTrack; cue: CaptionCue }[] = [];
    for (const track of captionTracks) {
      if (!(currentTime >= track.start && currentTime < track.start + track.duration)) continue;
      const local = currentTime - track.start;
      const cue = track.cues.find(c => local >= c.start && local < c.end);
      if (cue) out.push({ trackId: track.id, track, cue });
    }
    return out;
  }, [captionTracks, currentTime]);

  // ---- Export ----
  const [exporting, setExporting] = useState(false);
  const [exportProgress, setExportProgress] = useState(0);
  const [exportPhase, setExportPhase] = useState<'render' | 'upload' | 'server' | null>(null);
  const [showExportOptions, setShowExportOptions] = useState(false);
  const [exportOpts, setExportOpts] = useState<{
    resolution: '720p' | '1080p' | '4k';
    bitrate: number; // Mbps
    fps: 24 | 30 | 60;
    format: 'webm' | 'webm-vp9' | 'mp4';
  }>({ resolution: '720p', bitrate: 5, fps: 30, format: 'mp4' });
  // Onde renderizar: servidor (rápido, MP4 garantido, pode sair da tela) ou
  // navegador (fallback — necessário pra transições avançadas 100% fiéis).
  const [renderMode, setRenderMode] = useState<'server' | 'browser'>(serverRenderAvailable ? 'server' : 'browser');
  useEffect(() => {
    setRenderMode(serverRenderAvailable ? 'server' : 'browser');
  }, [serverRenderAvailable]);

  // Render server-side: manda a timeline inteira e acompanha o job na fila.
  const exportOnServer = async () => {
    if (videoClips.length === 0) { toast.error(t('videoStudio.editor.errEmpty')); return; }
    setExporting(true); setExportProgress(0); setExportPhase('server'); stopPlayback();
    toast('Renderizando no servidor — você pode continuar navegando, o vídeo aparece na galeria.', { icon: '🚀', duration: 6000 });
    try {
      const res = await videoStudioService.renderOnServer({
        timeline: { videoClips, audioClips, overlayClips, captionTracks, aspectRatio },
        resolution: exportOpts.resolution,
        fps: exportOpts.fps,
        bitrate: exportOpts.bitrate,
        title: projectName || undefined
      }, (pct) => setExportProgress(Math.round(pct)));
      if (res.success && res.data) {
        toast.success(t('videoStudio.editor.exported') as string);
        onExported(res.data);
      }
    } catch (e: any) {
      const msg = e.response?.data?.message || e.message || (t('videoStudio.editor.errExport') as string);
      // Timeline com mídia fora do nosso storage (ex.: URL externa) → o servidor
      // recusa; o export no navegador ainda funciona pra esse caso.
      toast.error(`${msg}${/plataforma|indisponível/i.test(msg) ? ' — tente "Renderizar no navegador".' : ''}`, { duration: 7000 });
    } finally {
      setExporting(false); setExportProgress(0); setExportPhase(null);
    }
  };

  const exportProject = async () => {
    if (videoClips.length === 0) { toast.error(t('videoStudio.editor.errEmpty')); return; }
    if (!('MediaRecorder' in window)) { toast.error(t('videoStudio.editor.errBrowser')); return; }

    setExporting(true); setExportProgress(0); setExportPhase('render'); stopPlayback();
    // O render usa rAF + captureStream em tempo real: se a aba perde o foco o
    // browser congela os frames e o arquivo sai corrompido/travado. Wake lock
    // evita a tela apagar, e o listener avisa o user se ele trocar de aba.
    let wakeLock: any = null;
    try { wakeLock = await (navigator as any).wakeLock?.request?.('screen'); } catch { /* not supported */ }
    const onHidden = () => {
      if (document.hidden) {
        toast('Volte para esta aba! O render pausa em segundo plano.', { icon: '⚠️', id: 'export-hidden', duration: 6000 });
      }
    };
    document.addEventListener('visibilitychange', onHidden);
    let audioCtx: AudioContext | null = null;
    try {
      const canvas = document.createElement('canvas');
      // Compute output dimensions from chosen resolution + aspect ratio
      const RES_HEIGHT: Record<string, number> = { '720p': 720, '1080p': 1080, '4k': 2160 };
      const outH = RES_HEIGHT[exportOpts.resolution] || 720;
      const aspectVal = (() => {
        const [a, b] = aspectRatio.split(':').map(Number);
        return a / b;
      })();
      canvas.height = outH;
      canvas.width = Math.round(outH * aspectVal);
      const ctx = canvas.getContext('2d')!;

      const AudioCtxClass = (window.AudioContext || (window as any).webkitAudioContext) as typeof AudioContext;
      audioCtx = new AudioCtxClass();
      const audioDest = audioCtx.createMediaStreamDestination();

      // Ordem de desenho = ordem visual do preview: lanes menores embaixo,
      // maiores por cima (o preview usa zIndex por lane; aqui é a ordem do draw).
      // Vídeos e overlays compartilham a mesma pilha de camadas na timeline —
      // a ordem de desenho tem que respeitar isso, não o tipo do clipe.
      const visualByLane: ({ kind: 'video'; clip: VideoClip } | { kind: 'overlay'; clip: TextOverlayClip | ImageOverlayClip })[] = [
        ...videoClips.map(c => ({ kind: 'video' as const, clip: c })),
        ...overlayClips.map(c => ({ kind: 'overlay' as const, clip: c }))
      ].sort((a, b) => (a.clip.lane || 0) - (b.clip.lane || 0));

      const vEls = new Map<string, HTMLVideoElement>();
      for (const clip of videoClips) {
        const v = document.createElement('video');
        v.crossOrigin = 'anonymous'; v.src = corsSafeUrl(clip.url); v.preload = 'auto';
        v.muted = clip.muted; v.volume = clip.volume ?? 1;
        await new Promise<void>((resolve, reject) => {
          v.onloadeddata = () => resolve();
          v.onerror = () => reject(new Error(t('videoStudio.editor.errLoadScene') as string));
        });
        if (!clip.muted) {
          try {
            const src = audioCtx.createMediaElementSource(v);
            const gain = audioCtx.createGain();
            gain.gain.value = clip.volume ?? 1;
            src.connect(gain).connect(audioDest);
          } catch {}
        }
        vEls.set(clip.id, v);
      }

      const aEls = new Map<string, HTMLAudioElement>();
      for (const clip of audioClips) {
        const a = document.createElement('audio');
        a.crossOrigin = 'anonymous'; a.src = corsSafeUrl(clip.url); a.preload = 'auto';
        await new Promise<void>((resolve, reject) => {
          a.onloadeddata = () => resolve();
          a.onerror = () => reject(new Error('Falha ao carregar áudio'));
        });
        try {
          const src = audioCtx.createMediaElementSource(a);
          const gain = audioCtx.createGain();
          gain.gain.value = clip.volume ?? 1;
          src.connect(gain).connect(audioDest);
        } catch {}
        aEls.set(clip.id, a);
      }

      const imgEls = new Map<string, HTMLImageElement>();
      for (const clip of overlayClips) {
        if (clip.kind !== 'image') continue;
        const img = new Image(); img.crossOrigin = 'anonymous';
        await new Promise<void>((resolve, reject) => {
          img.onload = () => resolve();
          img.onerror = () => reject(new Error('Falha ao carregar imagem'));
          img.src = corsSafeUrl((clip as ImageOverlayClip).url);
        });
        imgEls.set(clip.id, img);
      }

      const canvasStream = (canvas as any).captureStream(exportOpts.fps) as MediaStream;
      const combinedStream = new MediaStream([
        ...canvasStream.getVideoTracks(), ...audioDest.stream.getAudioTracks()
      ]);
      // Pick MIME based on user choice + browser support, with sensible fallbacks
      const mimeCandidates = exportOpts.format === 'mp4'
        ? ['video/mp4;codecs=avc1.42E01E,mp4a.40.2', 'video/mp4', 'video/webm;codecs=vp9,opus', 'video/webm']
        : exportOpts.format === 'webm-vp9'
          ? ['video/webm;codecs=vp9,opus', 'video/webm;codecs=vp9', 'video/webm']
          : ['video/webm;codecs=vp8,opus', 'video/webm'];
      const mime = mimeCandidates.find(m => MediaRecorder.isTypeSupported(m)) || 'video/webm';
      const recorder = new MediaRecorder(combinedStream, {
        mimeType: mime,
        videoBitsPerSecond: exportOpts.bitrate * 1_000_000,
        audioBitsPerSecond: 192_000
      });
      const chunks: Blob[] = [];
      recorder.ondataavailable = (e) => { if (e.data.size > 0) chunks.push(e.data); };
      const recordingDone = new Promise<Blob>((resolve, reject) => {
        recorder.onstop = () => resolve(new Blob(chunks, { type: mime }));
        recorder.onerror = (e: any) => reject(e?.error || new Error('Recording error'));
      });

      try { await audioCtx.resume(); } catch {}
      recorder.start();

      // Captura 1 frame do meio do render como thumbnail — sem isso a galeria
      // fica sem capa e precisa baixar o metadata do vídeo inteiro pra mostrar o card.
      let thumbBlob: Blob | null = null;
      let thumbRequested = false;
      const thumbAt = Math.min(1, totalDuration / 2);

      const startTs = performance.now();
      await new Promise<void>((resolve) => {
        const loop = () => {
          const tt = (performance.now() - startTs) / 1000;
          if (tt >= totalDuration) { resolve(); return; }
          setExportProgress(Math.min(99, Math.round((tt / totalDuration) * 100)));

          ctx.fillStyle = '#000'; ctx.fillRect(0, 0, canvas.width, canvas.height);

          // Áudio primeiro: não desenha nada, só mantém os elementos em sincronia.
          for (const clip of audioClips) {
            const a = aEls.get(clip.id); if (!a) continue;
            const inRange = tt >= clip.start && tt < clip.start + clip.duration;
            if (inRange) {
              const localT = (tt - clip.start) + (clip.trim_start || 0);
              if (Math.abs(a.currentTime - localT) > 0.5) a.currentTime = localT;
              if (a.paused) a.play().catch(() => {});
            } else if (!a.paused) a.pause();
          }

          // Vídeos e overlays desenhados na MESMA ordem de camadas do preview:
          // antes os vídeos vinham todos primeiro, então um vídeo na camada de
          // cima aparecia ATRÁS de uma imagem da camada de baixo no arquivo final.
          for (const item of visualByLane) {
            if (item.kind === 'video') {
              const clip = item.clip;

            if (!(tt >= clip.start && tt < clip.start + clip.duration)) continue;
            const v = vEls.get(clip.id); if (!v) continue;
            const localT = (tt - clip.start) + (clip.trim_start || 0);
            if (Math.abs(v.currentTime - localT) > 0.5) v.currentTime = localT;
            if (v.paused) v.play().catch(() => {});
            // Match the preview's `object-contain` behaviour: fit the video
            // inside the frame keeping its aspect ratio, letterboxing whatever
            // doesn't fill (the canvas was already painted black above).
            // Previously the export did the opposite (cover) — which forced
            // e.g. a 1080x1920 source to upscale ~1.8x to fill a 1920x1080
            // timeline before being cropped, destroying quality and showing
            // a different framing than what the user saw while editing.
            const vRatio = v.videoWidth / v.videoHeight;
            const cRatio = canvas.width / canvas.height;
            let dw, dh;
            if (vRatio > cRatio) { dw = canvas.width; dh = dw / vRatio; }
            else { dh = canvas.height; dw = dh * vRatio; }
            const dx = (canvas.width - dw) / 2;
            const dy = (canvas.height - dh) / 2;
            const filterCss = FILTER_PRESETS.find(p => p.id === (clip.filter || 'none'))?.css || 'none';

            // Transition state for first `transition_duration` seconds of the clip.
            // We apply REAL per-frame transforms (scale/rotate/blur/clip/alpha) instead
            // of just painting a black rect, so the export looks like the preview.
            const txDur = clip.transition_duration ?? 0.5;
            const since = tt - clip.start;
            const inTx = clip.transition && clip.transition !== 'cut' && since < txDur;
            const t01 = inTx ? Math.max(0, Math.min(1, since / txDur)) : 1;
            const inv = 1 - t01;

            ctx.save();
            try {
              const cw = canvas.width, ch = canvas.height;
              let alpha = 1;
              let txFilter: string | null = null;

              if (inTx) {
                switch (clip.transition) {
                  case 'fade':
                    alpha = t01;
                    break;
                  case 'slide':
                    ctx.translate(-cw * inv, 0);
                    break;
                  case 'wipe':
                    ctx.beginPath();
                    ctx.rect(0, 0, cw * t01, ch);
                    ctx.clip();
                    break;
                  case 'zoom':
                    alpha = t01;
                    ctx.translate(cw / 2, ch / 2);
                    ctx.scale(0.2 + 0.8 * t01, 0.2 + 0.8 * t01);
                    ctx.translate(-cw / 2, -ch / 2);
                    break;
                  case 'blur':
                    alpha = t01;
                    txFilter = `blur(${inv * 20}px)`;
                    break;
                  case 'spin':
                    alpha = t01;
                    ctx.translate(cw / 2, ch / 2);
                    ctx.rotate(inv * -Math.PI);
                    ctx.scale(0.5 + 0.5 * t01, 0.5 + 0.5 * t01);
                    ctx.translate(-cw / 2, -ch / 2);
                    break;
                  case 'flip':
                    alpha = t01;
                    ctx.translate(cw / 2, ch / 2);
                    // Approximate Y-axis flip in 2D: horizontal squeeze 0 → 1
                    ctx.scale(t01, 1);
                    ctx.translate(-cw / 2, -ch / 2);
                    break;
                  case 'iris':
                    ctx.beginPath();
                    ctx.arc(cw / 2, ch / 2, t01 * Math.hypot(cw, ch) / 2, 0, Math.PI * 2);
                    ctx.clip();
                    break;
                }
              }

              ctx.globalAlpha = alpha;
              // Combine the color filter with the optional transition blur
              if (filterCss !== 'none' && txFilter) ctx.filter = `${filterCss} ${txFilter}`;
              else if (txFilter) ctx.filter = txFilter;
              else ctx.filter = filterCss;

              try { ctx.drawImage(v, dx, dy, dw, dh); } catch {}
            } finally {
              ctx.restore();
              ctx.filter = 'none';
              ctx.globalAlpha = 1;
            }
            } else {
              const clip = item.clip;

            if (!(tt >= clip.start && tt < clip.start + clip.duration)) continue;
            if (clip.kind === 'text') {
              const o = clip as TextOverlayClip;
              // font_size is authored at FONT_REFERENCE_HEIGHT — scale to the
              // current canvas height so 720p / 1080p / 4k render the same
              // proportions as the preview (which uses the same scale logic).
              const fontScale = canvas.height / FONT_REFERENCE_HEIGHT;
              const fontPx = o.font_size * fontScale;
              const coords = getTextCoords(o);
              const cx = (canvas.width * coords.x) / 100;
              const cy = (canvas.height * coords.y) / 100;
              ctx.font = `${o.bold ? 'bold ' : ''}${o.italic ? 'italic ' : ''}${fontPx}px ${o.font_family}, system-ui, sans-serif`;
              ctx.textAlign = 'center';
              ctx.textBaseline = 'middle';

              // Word-wrap to 90% of canvas width — matches the preview's
              // maxWidth: 95% (minus the bg padding) and prevents huge text
              // from overflowing the frame in export.
              const maxLineWidth = canvas.width * 0.9 - fontPx * 0.6;
              const lines: string[] = [];
              for (const paragraph of o.text.split('\n')) {
                const words = paragraph.split(/\s+/).filter(Boolean);
                if (words.length === 0) { lines.push(''); continue; }
                let current = '';
                for (const word of words) {
                  const test = current ? `${current} ${word}` : word;
                  if (ctx.measureText(test).width > maxLineWidth && current) {
                    lines.push(current);
                    current = word;
                  } else {
                    current = test;
                  }
                }
                if (current) lines.push(current);
              }

              const lineH = fontPx * 1.25;
              const blockH = lineH * lines.length;
              const padX = fontPx * 0.3;
              const padY = fontPx * 0.1;

              if (o.background && o.background !== 'transparent') {
                const widest = Math.max(...lines.map(l => ctx.measureText(l).width));
                const bw = widest + padX * 2;
                const bh = blockH + padY * 2;
                ctx.fillStyle = o.background;
                const radius = fontPx * 0.1;
                if (typeof ctx.roundRect === 'function') {
                  ctx.beginPath();
                  ctx.roundRect(cx - bw / 2, cy - bh / 2, bw, bh, radius);
                  ctx.fill();
                } else {
                  ctx.fillRect(cx - bw / 2, cy - bh / 2, bw, bh);
                }
              }

              const firstLineY = cy - blockH / 2 + lineH / 2;
              for (let i = 0; i < lines.length; i++) {
                const ly = firstLineY + i * lineH;
                ctx.fillStyle = 'rgba(0,0,0,0.6)';
                ctx.fillText(lines[i], cx + 2, ly + 2);
                ctx.fillStyle = o.color;
                ctx.fillText(lines[i], cx, ly);
              }
            } else if (clip.kind === 'image') {
              const o = clip as ImageOverlayClip;
              const img = imgEls.get(clip.id); if (!img) continue;
              const w = (canvas.width * o.width_pct) / 100;
              const h = w * (img.naturalHeight / img.naturalWidth);
              const x = (canvas.width * o.position_x_pct) / 100 - w / 2;
              const y = (canvas.height * o.position_y_pct) / 100 - h / 2;
              ctx.globalAlpha = o.opacity;
              ctx.drawImage(img, x, y, w, h);
              ctx.globalAlpha = 1;
            }
            }
          }

          // Caption tracks — find the cue currently active in each track and render
          // it with the track's shared style. Uses the SAME % positioning + font
          // scaling as the preview overlay so what you see on screen is exactly
          // what lands in the exported file, at any output resolution.
          for (const track of captionTracks) {
            if (!(tt >= track.start && tt < track.start + track.duration)) continue;
            const local = tt - track.start;
            const cue = track.cues.find(c => local >= c.start && local < c.end);
            if (!cue || !cue.text) continue;

            const s = track.style;
            const fontScale = canvas.height / FONT_REFERENCE_HEIGHT;
            const fontPx = s.font_size * fontScale;
            const yPct = CAPTION_POSITION_Y_PCT[s.position] ?? 92;
            const cx = canvas.width / 2;
            const cy = (canvas.height * yPct) / 100;

            ctx.font = `${s.bold ? 'bold ' : ''}${s.italic ? 'italic ' : ''}${fontPx}px ${s.font_family}, system-ui, sans-serif`;
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';

            // Word-wrap to 90% of canvas width — matches the preview wrapper width
            const maxLineWidth = canvas.width * 0.9 - fontPx * 0.6;
            const lines: string[] = [];
            for (const paragraph of cue.text.split('\n')) {
              const words = paragraph.split(/\s+/).filter(Boolean);
              if (words.length === 0) { lines.push(''); continue; }
              let current = '';
              for (const word of words) {
                const test = current ? `${current} ${word}` : word;
                if (ctx.measureText(test).width > maxLineWidth && current) {
                  lines.push(current);
                  current = word;
                } else {
                  current = test;
                }
              }
              if (current) lines.push(current);
            }

            const lineH = fontPx * 1.25;
            const blockH = lineH * lines.length;
            const padX = fontPx * 0.3;
            const padY = fontPx * 0.1;

            // Background pill behind the whole block, centred on (cx, cy)
            if (s.background && s.background !== 'transparent') {
              const widest = Math.max(...lines.map(l => ctx.measureText(l).width));
              const bw = widest + padX * 2;
              const bh = blockH + padY * 2;
              const radius = fontPx * 0.1;
              ctx.fillStyle = s.background;
              if (typeof ctx.roundRect === 'function') {
                ctx.beginPath();
                ctx.roundRect(cx - bw / 2, cy - bh / 2, bw, bh, radius);
                ctx.fill();
              } else {
                ctx.fillRect(cx - bw / 2, cy - bh / 2, bw, bh);
              }
            }

            const firstLineY = cy - blockH / 2 + lineH / 2;
            for (let i = 0; i < lines.length; i++) {
              const ly = firstLineY + i * lineH;
              ctx.fillStyle = 'rgba(0,0,0,0.7)';
              ctx.fillText(lines[i], cx + 2, ly + 2);
              ctx.fillStyle = s.color;
              ctx.fillText(lines[i], cx, ly);
            }
          }

          if (!thumbRequested && tt >= thumbAt) {
            thumbRequested = true;
            try { canvas.toBlob(b => { thumbBlob = b; }, 'image/jpeg', 0.8); } catch { /* thumb é best-effort */ }
          }

          requestAnimationFrame(loop);
        };
        loop();
      });

      recorder.stop();
      const blob = await recordingDone;
      setExportProgress(100);

      // Sobe a thumbnail capturada do canvas (best-effort — não trava o export)
      let thumbnailUrl: string | undefined;
      if (thumbBlob) {
        try {
          const thumbFile = new File([thumbBlob], 'composition-thumb.jpg', { type: 'image/jpeg' });
          const up = await videoStudioService.uploadImage(thumbFile);
          thumbnailUrl = up?.url;
        } catch { /* sem thumb, galeria cai no <video preload> */ }
      }

      // Formato REAL gravado (o recorder pode ter caído pra WebM) — é isso que
      // vai pro registro, não o que o user pediu no modal.
      const actualFormat: 'webm' | 'webm-vp9' | 'mp4' =
        /webm/i.test(mime) ? (/vp9/i.test(mime) ? 'webm-vp9' : 'webm') : 'mp4';

      // Switch to upload phase
      setExportPhase('upload');
      setExportProgress(0);
      toast.loading(t('videoStudio.editor.savingFinal') as string, { id: 'export-upload' });
      const res = await videoStudioService.uploadComposition(blob, {
        title: (projectName || t('videoStudio.editor.untitled', { count: videoClips.length }) as string),
        aspect_ratio: aspectRatio, duration: totalDuration,
        scenes: videoClips.map(c => ({ id: c.id, source_video_id: c.source_video_id, url: c.url, duration: c.duration })),
        thumbnail_url: thumbnailUrl,
        // Parâmetros de qualidade escolhidos pelo user — sem isso o backend salva
        // sem registro do que foi gerado (bug reportado).
        resolution: exportOpts.resolution,
        bitrate: exportOpts.bitrate,
        fps: exportOpts.fps,
        format: actualFormat,
      }, (pct) => setExportProgress(pct));
      toast.dismiss('export-upload');
      if (res.success && res.data) { toast.success(t('videoStudio.editor.exported') as string); onExported(res.data); }
    } catch (e: any) {
      toast.dismiss('export-upload');
      toast.error(e.message || t('videoStudio.editor.errExport') as string);
    } finally {
      try { audioCtx?.close(); } catch { /* já fechado */ }
      try { wakeLock?.release?.(); } catch { /* ok */ }
      document.removeEventListener('visibilitychange', onHidden);
      toast.dismiss('export-hidden');
      setExporting(false); setExportProgress(0); setExportPhase(null);
    }
  };

  const onTimelineBgClick = useCallback((e: React.MouseEvent) => {
    if (e.target !== e.currentTarget) return;
    if (e.button !== 0) return; // botão direito abre menu, não move o playhead
    // Clique em área vazia: além de desselecionar, posiciona o playhead ali —
    // antes só a régua fazia seek, o que ninguém descobre sozinho.
    setSelected(null);
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    const x = e.clientX - rect.left;
    seekTo(x / pxPerSecRef.current);
  }, [seekTo]);

  // Handlers estáveis pros ClipBlocks memoizados (React.memo) da timeline
  const onClipContextMenu = useCallback((e: React.MouseEvent, kind: 'video' | 'audio' | 'overlay', id: string) => {
    e.preventDefault();
    setSelected({ kind, id });
    setContextMenu({ x: e.clientX, y: e.clientY, kind, id });
  }, []);
  const onClipSelect = useCallback((kind: 'video' | 'audio' | 'overlay', id: string) => {
    setSelected({ kind, id });
  }, []);
  const openGallery = useCallback((src: 'video' | 'image') => setGalleryPicker(src), []);

  // Props estáveis pro painel de propriedades memoizado: sem isto, cada tick do
  // playhead re-renderizava o painel inteiro (sliders, inputs, botões).
  const onSelChange = useCallback((patch: any) => {
    if (selected) updateClip(selected.kind, selected.id, patch);
  }, [selected, updateClip]);
  const onSelRemove = useCallback(() => {
    if (selected) removeClip(selected.kind, selected.id);
  }, [selected, removeClip]);
  // Ref no autoTranscribe (mesmo padrão do splitRef): o botão memoizado sempre
  // chama a versão mais recente sem invalidar o useMemo a cada render.
  const autoTranscribeRef = useRef<() => void>(() => {});
  useEffect(() => { autoTranscribeRef.current = () => autoTranscribe(); });
  const autoCaptionButton = useMemo(() => (
    <div>
      <label className="block text-[11px] font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Legendas automáticas</label>
      <button onClick={() => autoTranscribeRef.current()} disabled={transcribing}
        className="w-full inline-flex items-center justify-center gap-2 py-2 rounded-lg bg-gradient-to-r from-fuchsia-500/15 to-violet-500/15 border border-fuchsia-500/40 text-fuchsia-700 dark:text-fuchsia-300 text-xs font-semibold hover:brightness-110 disabled:opacity-50">
        {transcribing
          ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Transcrevendo…</>
          : <><Mic className="w-3.5 h-3.5" /> Gerar do áudio (Whisper)</>}
      </button>
      <p className="text-[10px] text-gray-500 mt-1">Custa 2 créditos por minuto. Requer áudio no vídeo.</p>
    </div>
  ), [transcribing]);

  // Welcome / project selection screen — shown until user starts or loads
  if (!hasStarted) {
    return (
      <>
        <div className="rounded-2xl bg-gradient-to-br from-gray-950 via-purple-950 to-gray-900 border border-purple-900/40 p-8 sm:p-12 text-center shadow-2xl">
          <div className="max-w-2xl mx-auto">
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-primary-500 to-purple-500 inline-flex items-center justify-center mx-auto mb-5 shadow-lg shadow-primary-500/30">
              <Sparkles className="w-8 h-8 text-white" />
            </div>
            <h2 className="text-2xl sm:text-3xl font-bold text-white mb-2">Editor de vídeo</h2>
            <p className="text-gray-300 text-sm sm:text-base mb-8 max-w-md mx-auto">
              Crie um vídeo combinando cenas, áudio, imagens e legendas. Comece um projeto novo ou abra um salvo.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <button
                onClick={() => {
                  // Reset completo pra evitar vazar estado de um projeto anterior
                  // pro novo (causa do bug "save sobrescreve outro projeto" reportado).
                  setVideoClips([]); setAudioClips([]); setOverlayClips([]); setCaptionTracks([]);
                  setHasStarted(true); setProjectId(null); setProjectName('Novo projeto');
                  setSelected(null); setCurrentTime(0); setLastSavedAt(null);
                  resetHistory();
                }}
                className="p-6 rounded-2xl bg-gradient-to-br from-primary-500/25 to-purple-500/15 border-2 border-primary-500/60 hover:from-primary-500/40 hover:to-purple-500/30 hover:border-primary-400 transition text-left group shadow-lg"
              >
                <div className="w-12 h-12 rounded-xl bg-primary-500 inline-flex items-center justify-center mb-3 group-hover:scale-110 transition shadow-md">
                  <Sparkles className="w-6 h-6 text-white" />
                </div>
                <p className="text-base font-bold text-white mb-1">Novo projeto</p>
                <p className="text-xs text-gray-200">Comece do zero com a timeline vazia</p>
              </button>
              <button
                onClick={openProjectsList}
                className="p-6 rounded-2xl bg-gradient-to-br from-amber-500/25 to-orange-500/15 border-2 border-amber-500/60 hover:from-amber-500/40 hover:to-orange-500/30 hover:border-amber-400 transition text-left group shadow-lg"
              >
                <div className="w-12 h-12 rounded-xl bg-amber-500 inline-flex items-center justify-center mb-3 group-hover:scale-110 transition shadow-md">
                  <FolderOpen className="w-6 h-6 text-white" />
                </div>
                <p className="text-base font-bold text-white mb-1">Abrir projeto salvo</p>
                <p className="text-xs text-gray-200">Continue de onde parou ou abra um modelo</p>
              </button>
            </div>
          </div>
        </div>

        {/* Reuse the saved-projects modal from below */}
        {showProjects && (
          <div className="fixed inset-0 z-[150] flex items-center justify-center p-4 bg-gray-950/80 backdrop-blur-sm" onClick={() => setShowProjects(false)}>
            <div className="w-full max-w-2xl max-h-[80vh] overflow-hidden bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-2xl shadow-2xl flex flex-col" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-800">
                <h2 className="text-base font-bold text-gray-900 dark:text-white inline-flex items-center gap-2">
                  <FolderOpen className="w-4 h-4" /> Projetos salvos
                </h2>
                <button onClick={() => setShowProjects(false)} className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-white/5 text-gray-600 dark:text-gray-400">
                  <X className="w-4 h-4" />
                </button>
              </div>
              <div className="overflow-y-auto p-3 space-y-2">
                {savedProjects.length === 0 ? (
                  <p className="p-8 text-center text-sm text-gray-500">Nenhum projeto salvo ainda.</p>
                ) : savedProjects.map(p => (
                  <button key={p.id} onClick={() => loadProject(p.id)}
                    className="w-full flex items-center gap-3 p-3 rounded-lg bg-gray-50 dark:bg-gray-900/50 border border-gray-200 dark:border-gray-800 hover:border-primary-500/50 hover:bg-gray-100 dark:hover:bg-gray-900/80 transition text-left"
                  >
                    {p.thumbnail_url
                      ? <img src={p.thumbnail_url} alt="" className="w-16 h-10 object-cover rounded bg-black flex-shrink-0" />
                      : <div className="w-16 h-10 rounded bg-gray-200 dark:bg-gray-800 flex items-center justify-center flex-shrink-0"><Film className="w-4 h-4 text-gray-500" /></div>}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-gray-900 dark:text-white truncate">{p.name}</p>
                      <p className="text-[11px] text-gray-500">
                        {p.aspect_ratio} · {p.duration_seconds ? `${Math.round(p.duration_seconds)}s` : '—'}
                        {p.is_template && <span className="ml-1 text-amber-500">★ Modelo</span>}
                      </p>
                    </div>
                    <button onClick={(e) => removeProject(p.id, e)} className="p-1.5 rounded text-gray-400 hover:bg-rose-500/10 hover:text-rose-600">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}
      </>
    );
  }

  return (
    <div className="space-y-3">
      {/* Top mini-bar: project name + save / clear / back actions.
          On mobile stacks the actions row below the name to prevent overflow. */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 px-3 py-2 rounded-xl bg-white dark:bg-gray-900/50 border border-gray-200 dark:border-gray-800">
        <div className="flex items-center gap-2 min-w-0 flex-1">
          <button
            onClick={() => {
              const reset = () => {
                setVideoClips([]); setAudioClips([]); setOverlayClips([]); setCaptionTracks([]);
                setProjectId(null); setProjectName('Novo projeto');
                setHasStarted(false); setSelected(null); setCurrentTime(0);
                setLastSavedAt(null); resetHistory();
              };
              if (videoClips.length || audioClips.length || overlayClips.length) {
                askConfirm({
                  title: 'Sair do projeto?',
                  body: 'Você tem mudanças não salvas que serão perdidas. Quer continuar mesmo assim?',
                  danger: true, confirmLabel: 'Sair sem salvar',
                  onConfirm: reset
                });
              } else reset();
            }}
            className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-white/5 text-gray-500 hover:text-gray-900 dark:hover:text-white"
            title="Voltar pra seleção de projetos"
          >
            <FolderOpen className="w-4 h-4" />
          </button>
          <input
            type="text" value={projectName}
            onChange={(e) => setProjectName(e.target.value)}
            onBlur={() => projectId && saveProject(false)}
            placeholder="Nome do projeto"
            className="text-sm font-semibold text-gray-900 dark:text-white bg-transparent border-0 outline-none focus:bg-gray-100 dark:focus:bg-white/5 rounded px-2 py-0.5 flex-1 sm:flex-initial sm:min-w-[180px] min-w-0"
            title="Clique pra editar (salva ao sair do campo)"
          />
          <SaveIndicator
            projectId={projectId}
            autoSaving={autoSavingProject}
            saving={savingProject}
            lastSavedAt={lastSavedAt}
            tick={savedAgoTick}
          />
        </div>
        {/* Action row — wraps on mobile, label hidden under sm */}
        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={() => saveProject(false)}
            disabled={savingProject}
            className="px-3 py-1.5 rounded-lg bg-gradient-to-r from-emerald-500 to-green-600 text-white text-xs font-semibold inline-flex items-center gap-1.5 hover:brightness-110 disabled:opacity-50 shadow-sm"
            title="Salvar projeto (Ctrl+S)"
          >
            {savingProject ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
            <span className="hidden sm:inline">Salvar projeto</span>
            <span className="sm:hidden">Salvar</span>
          </button>
          <button
            onClick={openProjectsList}
            className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-white/5 text-gray-500 hover:text-gray-900 dark:hover:text-white"
            title="Abrir outro projeto"
          >
            <FolderOpen className="w-4 h-4" />
          </button>
          <div className="w-px h-5 bg-gray-300 dark:bg-gray-700" />
          <button
            onClick={() => askConfirm({
              title: 'Limpar timeline',
              body: 'Vai remover todos os clipes da timeline e começar um rascunho novo. O projeto salvo continua intacto — reabra ele pelo ícone de pasta.',
              danger: true, confirmLabel: 'Limpar tudo',
              onConfirm: () => {
                setVideoClips([]); setAudioClips([]); setOverlayClips([]); setCaptionTracks([]);
                setSelected(null); setCurrentTime(0);
                // Solta o vínculo com o projeto salvo: mantendo o projectId, o
                // auto-save gravava a timeline VAZIA por cima dele 3s depois —
                // exatamente o oposto do que este diálogo promete.
                setProjectId(null); setProjectName('Novo projeto'); setLastSavedAt(null);
                resetHistory();
              }
            })}
            className="px-2 py-1 rounded text-[11px] text-rose-600 dark:text-rose-400 hover:bg-rose-500/10 inline-flex items-center gap-1"
            title="Limpar timeline"
          >
            <Trash2 className="w-3 h-3" /> Limpar
          </button>
        </div>
      </div>

      {/* === TOP ROW: Library / Preview / Properties === */}
      <div className="grid grid-cols-1 xl:grid-cols-12 gap-3">
        {/* Library */}
        <div className="xl:col-span-3 rounded-2xl bg-white dark:bg-gray-900/40 border border-gray-200 dark:border-gray-800 p-3">
          <div className="flex items-center gap-2 mb-3 text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">
            <Library className="w-3.5 h-3.5" />
            {t('videoStudio.editor.library')}
          </div>
          <MediaLibrary onPick={addVideoFromLibrary} onOpenGallery={openGallery} refreshKey={mediaRefreshKey} />

          <div className="mt-4">
            <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider mb-1.5">{t('videoStudio.editor.actions.title')}</p>
            <div className="grid grid-cols-3 gap-1.5">
              <button onClick={() => setShowAIEdit(true)}
                title={t('videoStudio.editor.actions.aiTooltip') as string}
                className="relative flex flex-col items-center justify-center gap-1 px-2 py-2.5 rounded-lg bg-gradient-to-br from-fuchsia-500 to-purple-600 text-white text-[10px] font-semibold hover:brightness-110 shadow-sm transition">
                <Sparkles className="w-4 h-4" />
                <span>{t('videoStudio.editor.actions.ai')}</span>
              </button>
              <button onClick={addText}
                title={t('videoStudio.editor.actions.addTextTooltip') as string}
                className="relative flex flex-col items-center justify-center gap-1 px-2 py-2.5 rounded-lg bg-amber-500/10 border border-amber-500/40 text-amber-700 dark:text-amber-300 text-[10px] font-semibold hover:bg-amber-500/20 transition">
                <TypeIcon className="w-4 h-4" />
                <span>{t('videoStudio.editor.actions.addText')}</span>
              </button>
              <button onClick={() => setShowCaptionsEditor(true)}
                title={t('videoStudio.editor.actions.captionsTooltip') as string}
                className="relative flex flex-col items-center justify-center gap-1 px-2 py-2.5 rounded-lg bg-fuchsia-500/10 border border-fuchsia-500/40 text-fuchsia-700 dark:text-fuchsia-300 text-[10px] font-semibold hover:bg-fuchsia-500/20 transition">
                <TypeIcon className="w-4 h-4" />
                <span>{t('videoStudio.editor.actions.captions')}</span>
                {captionTracks.length > 0 && (
                  <span className="absolute -top-1.5 -right-1.5 min-w-[16px] h-4 px-1 rounded-full bg-fuchsia-500 text-white text-[9px] font-bold inline-flex items-center justify-center">
                    {captionTracks.length}
                  </span>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Preview — capped height so vertical videos don't blow up the viewport */}
        <div className="xl:col-span-6 rounded-2xl bg-black overflow-hidden border border-gray-200 dark:border-gray-800 relative flex flex-col" style={{ height: 'min(70vh, 600px)' }}>
          <div className="flex-1 flex items-center justify-center min-h-0 bg-black p-2">
            <div
              ref={stageRef}
              className="relative bg-black"
              style={{
                aspectRatio: aspectRatio.replace(':', ' / '),
                maxWidth: '100%', maxHeight: '100%',
                width: 'auto', height: '100%'
              }}
            >
            {/* Performance: só MONTA o <video> quando o playhead está perto do clip
                (janela de 2s antes / 1s depois). Antes, 10 clipes = 10 <video>
                paralelos decodificando = lag severo na CPU mesmo escondidos.
                Pra clips fora dessa janela, NADA é renderizado. */}
            {videoClips.map(clip => {
              const nearRange = currentTime >= clip.start - 2 && currentTime < clip.start + clip.duration + 1;
              if (!nearRange) return null;
              const inRange = currentTime >= clip.start && currentTime < clip.start + clip.duration;
              const baseFilter = FILTER_PRESETS.find(p => p.id === (clip.filter || 'none'))?.css || 'none';
              // Pular cálculo de transition pra clips ainda fora do range (preload only)
              const tx = inRange ? computeTransitionStyle(clip, currentTime, baseFilter) : {};
              return (
                <video key={clip.id}
                  ref={(el) => { if (el) videoElRefs.current.set(clip.id, el); else videoElRefs.current.delete(clip.id); }}
                  src={clip.url} muted={clip.muted} playsInline preload="metadata"
                  className="absolute inset-0 w-full h-full object-contain"
                  style={{
                    visibility: inRange ? 'visible' : 'hidden',
                    filter: tx.filter ?? baseFilter,
                    opacity: tx.opacity,
                    transform: tx.transform,
                    clipPath: tx.clipPath,
                    transformOrigin: 'center center',
                    // Mesmo empilhamento do export: lane maior fica por cima.
                    // Vídeos e overlays compartilham a escala (empate → overlay,
                    // que é renderizado depois no DOM).
                    zIndex: 10 + (clip.lane || 0)
                  }} />
              );
            })}
            {/* Mesma estratégia pro áudio: só monta quando próximo do range,
                preload metadata em vez de auto (evita download do arquivo inteiro). */}
            {audioClips.map(clip => {
              const nearRange = currentTime >= clip.start - 2 && currentTime < clip.start + clip.duration + 1;
              if (!nearRange) return null;
              return (
                <audio key={clip.id}
                  ref={(el) => { if (el) audioElRefs.current.set(clip.id, el); else audioElRefs.current.delete(clip.id); }}
                  src={clip.url} preload="metadata" />
              );
            })}
            {activeOverlays.map(o => o.kind === 'text' ? (
              <DraggableTextOverlay
                key={o.id}
                clip={o as TextOverlayClip}
                stageHeight={stageHeight}
                currentTime={currentTime}
                isSelected={selected?.kind === 'overlay' && selected.id === o.id}
                onSelect={() => setSelected({ kind: 'overlay', id: o.id })}
                onChange={(patch) => updateClip('overlay', o.id, patch)}
              />
            ) : (
              <DraggableImageOverlay
                key={o.id}
                clip={o as ImageOverlayClip}
                currentTime={currentTime}
                isSelected={selected?.kind === 'overlay' && selected.id === o.id}
                onSelect={() => setSelected({ kind: 'overlay', id: o.id })}
                onChange={(patch) => updateClip('overlay', o.id, patch)}
              />
            ))}

            {/* Caption tracks — one cue per active track shown over the stage.
                Positioned with the SAME % math the canvas exporter uses, and
                font sized via FONT_REFERENCE_HEIGHT so preview & export match
                proportionally at any stage/canvas dimension. */}
            {activeCaptions.map(({ trackId, track, cue }) => {
              const yPct = CAPTION_POSITION_Y_PCT[track.style.position] ?? 92;
              const capFontScale = stageHeight > 0 ? stageHeight / FONT_REFERENCE_HEIGHT : 0.5;
              const capFontPx = track.style.font_size * capFontScale;
              const hasBg = track.style.background && track.style.background !== 'transparent';
              return (
                <div key={`cap-${trackId}`}
                  className="absolute pointer-events-none flex justify-center"
                  style={{
                    left: '50%',
                    top: `${yPct}%`,
                    transform: 'translate(-50%, -50%)',
                    width: '90%',
                    zIndex: 999
                  }}
                >
                  <span style={{
                    color: track.style.color,
                    fontSize: `${capFontPx}px`,
                    lineHeight: 1.25,
                    fontFamily: `${track.style.font_family}, system-ui, sans-serif`,
                    fontWeight: track.style.bold ? 700 : 600,
                    fontStyle: track.style.italic ? 'italic' : 'normal',
                    background: hasBg ? track.style.background : undefined,
                    padding: hasBg ? `${capFontPx * 0.1}px ${capFontPx * 0.3}px` : undefined,
                    borderRadius: hasBg ? `${capFontPx * 0.1}px` : undefined,
                    textShadow: '0 2px 8px rgba(0,0,0,0.6)',
                    whiteSpace: 'pre-line',
                    textAlign: 'center'
                  }}>{cue.text}</span>
                </div>
              );
            })}
            </div>
          </div>

          {/* Toolbar — wraps to two rows on mobile so nothing overflows */}
          <div className="p-2 flex items-center justify-between bg-gray-950/90 gap-2 flex-wrap">
            {/* Left group: playback + time + cortar */}
            <div className="flex items-center gap-2 min-w-0">
              <button onClick={() => playing ? stopPlayback() : setPlaying(true)}
                className="p-2 rounded-lg bg-white/10 text-white hover:bg-white/20"
                title={playing ? 'Pausar (espaço)' : 'Tocar (espaço)'}>
                {playing ? <Pause className="w-3.5 h-3.5" /> : <Play className="w-3.5 h-3.5" />}
              </button>
              <span className="text-[11px] text-gray-400 tabular-nums font-mono whitespace-nowrap">
                {fmtTime(currentTime)} / {fmtTime(totalDuration)}
              </span>
              {selected && (
                <button onClick={splitAtPlayhead} className="p-2 rounded bg-white/5 text-white hover:bg-white/10" title="Cortar no playhead (S)">
                  <Scissors className="w-3.5 h-3.5" />
                </button>
              )}
            </div>

            {/* Right group: zoom + project + export */}
            <div className="flex items-center gap-1 flex-wrap">
              {/* Zoom — hide on very small screens, the slider in the timeline is enough */}
              <div className="hidden sm:flex items-center">
                <button onClick={() => setPxPerSec(p => Math.max(15, p - 10))} className="p-1.5 rounded hover:bg-white/10 text-gray-400 hover:text-white" title="Diminuir zoom">
                  <ZoomOut className="w-3.5 h-3.5" />
                </button>
                <span className="text-[10px] text-gray-500 tabular-nums w-9 text-center">{pxPerSec}</span>
                <button onClick={() => setPxPerSec(p => Math.min(200, p + 10))} className="p-1.5 rounded hover:bg-white/10 text-gray-400 hover:text-white" title="Aumentar zoom">
                  <ZoomIn className="w-3.5 h-3.5" />
                </button>
                <div className="w-px h-5 bg-gray-700 mx-1" />
              </div>
              <ShortcutsHelp />
              <button onClick={openProjectsList} className="p-2 rounded hover:bg-white/10 text-gray-400 hover:text-white" title="Carregar projeto">
                <FolderOpen className="w-3.5 h-3.5" />
              </button>
              <button onClick={() => saveProject(false)} disabled={savingProject}
                className="p-2 rounded hover:bg-white/10 text-gray-400 hover:text-white" title="Salvar projeto">
                {savingProject ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
              </button>
              <button onClick={() => setShowExportOptions(true)} disabled={exporting}
                className="ml-1 px-3 py-1.5 rounded-lg bg-gradient-to-r from-primary-500 to-purple-600 text-white text-xs font-semibold inline-flex items-center gap-1.5 hover:brightness-110 disabled:opacity-50 whitespace-nowrap">
                {exporting
                  ? <><Loader2 className="w-3.5 h-3.5 animate-spin" />
                      {exportPhase === 'server' ? `Servidor ${exportProgress}%`
                        : exportPhase === 'upload' ? `Upload ${exportProgress}%`
                        : `Render ${exportProgress}%`}
                    </>
                  : <><Sparkles className="w-3.5 h-3.5" /> Exportar</>}
              </button>
            </div>
          </div>
        </div>

        {/* Properties */}
        <div className="xl:col-span-3 rounded-2xl bg-white dark:bg-gray-900/40 border border-gray-200 dark:border-gray-800 p-3">
          <p className="text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider mb-3">
            {t('videoStudio.editor.sceneProperties')}
          </p>
          {!sel ? (
            <p className="text-xs text-gray-500 text-center py-6">{t('videoStudio.editor.selectScene')}</p>
          ) : (
            <ClipProperties
              clip={sel} kind={selected!.kind}
              onChange={onSelChange}
              onRemove={onSelRemove}
              autoCaptionButton={autoCaptionButton}
            />
          )}
        </div>
      </div>

      {/* === BOTTOM: TIMELINE === */}
      <div className="rounded-2xl bg-white dark:bg-gray-900/40 border border-gray-200 dark:border-gray-800 overflow-hidden">
        <TimelineSurface
          totalDuration={totalDuration}
          pxPerSec={pxPerSec}
          currentTime={currentTime}
          setCurrentTime={seekTo}
          videoClips={videoClips}
          audioClips={audioClips}
          overlayClips={overlayClips}
          selected={selected}
          startDrag={startDrag}
          onBgClick={onTimelineBgClick}
          timelineWidth={timelineWidth}
          onContextMenu={onClipContextMenu}
          onSelectClip={onClipSelect}
          draggingId={draggingId}
          snapLine={snapLine}
          playing={playing}
          onZoomChange={setPxPerSec}
        />
      </div>

      {/* Confirm dialog */}
      {confirmDialog && (
        <div className="fixed inset-0 z-[300] flex items-center justify-center p-4 bg-gray-950/70 backdrop-blur-sm" onClick={() => setConfirmDialog(null)}>
          <div className="w-full max-w-sm bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-2xl shadow-2xl overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className={`p-5 border-b ${confirmDialog.danger ? 'border-rose-200 dark:border-rose-900/50' : 'border-gray-200 dark:border-gray-800'}`}>
              <div className="flex items-start gap-3">
                <div className={`flex-shrink-0 w-10 h-10 rounded-xl inline-flex items-center justify-center ${
                  confirmDialog.danger ? 'bg-rose-500/15 text-rose-600 dark:text-rose-400' : 'bg-primary-500/15 text-primary-600 dark:text-primary-400'
                }`}>
                  {confirmDialog.danger ? <Trash2 className="w-5 h-5" /> : <Sparkles className="w-5 h-5" />}
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-base font-bold text-gray-900 dark:text-white mb-1">{confirmDialog.title}</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400 whitespace-pre-line">{confirmDialog.body}</p>
                </div>
              </div>
            </div>
            <div className="p-3 flex justify-end gap-2 bg-gray-50 dark:bg-gray-900/50">
              <button onClick={() => setConfirmDialog(null)}
                className="px-4 py-2 rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-sm font-semibold text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700">
                Cancelar
              </button>
              <button onClick={() => { confirmDialog.onConfirm(); setConfirmDialog(null); }}
                className={`px-4 py-2 rounded-lg text-sm font-semibold text-white shadow-sm ${
                  confirmDialog.danger
                    ? 'bg-gradient-to-r from-rose-500 to-rose-600 hover:brightness-110'
                    : 'bg-gradient-to-r from-primary-500 to-purple-600 hover:brightness-110'
                }`}>
                {confirmDialog.confirmLabel || 'Confirmar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Prompt dialog */}
      {promptDialog && (
        <PromptDialog
          dialog={promptDialog}
          onClose={() => setPromptDialog(null)}
        />
      )}

      {/* External gallery picker */}
      <GalleryPicker
        open={galleryPicker !== null}
        source={galleryPicker}
        onClose={() => setGalleryPicker(null)}
        onPick={async (asset: GalleryAsset) => {
          // 1) Persist into THIS user's video studio library (no re-upload — same S3 URL)
          //    so it shows up in MediaLibrary for reuse without re-opening the gallery.
          let imported: GeneratedVideo | null = null;
          try {
            const r = await videoStudioService.importAsset({
              url: asset.url, title: asset.title, mime_type: asset.mime_type,
              duration: asset.duration_seconds, width: asset.width, height: asset.height
            });
            if (r.success && r.data) imported = r.data;
          } catch { /* fallback to ad-hoc add below */ }

          // 2) Refresh the library so the new item appears
          setMediaRefreshKey(k => k + 1);

          // 3) Add to the timeline. Use the imported record if we got one, else build ad-hoc.
          const forTimeline: GeneratedVideo = imported || ({
            id: asset.id, user_id: '', title: asset.title,
            provider: 'uploaded' as any, original_url: asset.url,
            thumbnail_url: asset.thumbnail_url, mime_type: asset.mime_type,
            duration_seconds: asset.duration_seconds,
            aspect_ratio: asset.width && asset.height
              ? (asset.width === asset.height ? '1:1' : asset.width > asset.height ? '16:9' : '9:16')
              : '16:9',
            credits_cost: 0, is_favorite: false,
            status: 'completed' as any, progress: 100,
            created_at: new Date().toISOString(), updated_at: new Date().toISOString()
          } as GeneratedVideo);
          addVideoFromLibrary(forTimeline);
        }}
      />

      {/* Captions editor — works with CaptionTrack (1 block, many cues) */}
      <CaptionsEditor
        open={showCaptionsEditor}
        onClose={() => setShowCaptionsEditor(false)}
        tracks={captionTracks}
        currentTime={currentTime}
        onSeek={seekTo}
        onAddTrack={(language) => addCaption(language)}
        onUpdateTrack={updateCaptionTrack}
        onRemoveTrack={removeCaptionTrack}
        onAddCue={addCueToTrack}
        onUpdateCue={updateCue}
        onRemoveCue={removeCueFromTrack}
        onImportSRT={importSRT}
        onExportSRT={exportSRT}
        onAutoTranscribe={() => autoTranscribe()}
        transcribing={transcribing}
      />

      {/* AI editor — natural-language timeline edits via Claude tool-use */}
      <AIEditChat
        isOpen={showAIEdit}
        onClose={() => setShowAIEdit(false)}
        timeline={{
          videoClips,
          audioClips,
          overlayClips,
          captionTracks,
          aspectRatio: aspectRatio as '16:9' | '9:16' | '1:1' | '4:3'
        }}
        onApply={(next) => {
          setVideoClips(next.videoClips);
          setAudioClips(next.audioClips);
          setOverlayClips(next.overlayClips);
          setCaptionTracks(next.captionTracks);
          if (next.aspectRatio !== aspectRatio && onAspectRatioChange) {
            onAspectRatioChange(next.aspectRatio);
          }
        }}
      />

      {/* Export options modal */}
      {showExportOptions && (
        <div className="fixed inset-0 z-[150] flex items-center justify-center p-4 bg-gray-950/80 backdrop-blur-sm" onClick={() => setShowExportOptions(false)}>
          <div className="w-full max-w-md bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-2xl shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-800">
              <h2 className="text-base font-bold text-gray-900 dark:text-white inline-flex items-center gap-2">
                <Sparkles className="w-4 h-4" /> Configurar exportação
              </h2>
              <button onClick={() => setShowExportOptions(false)} className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-white/5 text-gray-600 dark:text-gray-400">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="p-4 space-y-4">
              {serverRenderAvailable && (
                <div>
                  <label className="block text-[11px] font-semibold text-gray-500 uppercase tracking-wider mb-2">Onde renderizar</label>
                  <div className="grid grid-cols-2 gap-1">
                    <button onClick={() => setRenderMode('server')}
                      className={`py-2 px-2 rounded-lg text-xs font-semibold border transition text-left ${
                        renderMode === 'server'
                          ? 'bg-primary-500/20 border-primary-500/50 text-gray-900 dark:text-white'
                          : 'bg-white dark:bg-gray-900/40 border-gray-200 dark:border-gray-800 text-gray-600 dark:text-gray-400'
                      }`}>
                      <div>No servidor ⚡</div>
                      <div className="text-[9px] opacity-70 font-normal">Rápido, MP4 garantido, pode sair da tela</div>
                    </button>
                    <button onClick={() => setRenderMode('browser')}
                      className={`py-2 px-2 rounded-lg text-xs font-semibold border transition text-left ${
                        renderMode === 'browser'
                          ? 'bg-primary-500/20 border-primary-500/50 text-gray-900 dark:text-white'
                          : 'bg-white dark:bg-gray-900/40 border-gray-200 dark:border-gray-800 text-gray-600 dark:text-gray-400'
                      }`}>
                      <div>No navegador</div>
                      <div className="text-[9px] opacity-70 font-normal">Transições avançadas 100% fiéis</div>
                    </button>
                  </div>
                </div>
              )}
              <div>
                <label className="block text-[11px] font-semibold text-gray-500 uppercase tracking-wider mb-2">Resolução</label>
                <div className="grid grid-cols-3 gap-1">
                  {(['720p', '1080p', '4k'] as const).map(r => (
                    <button key={r} onClick={() => setExportOpts(o => ({ ...o, resolution: r }))}
                      className={`py-2 rounded-lg text-xs font-semibold border transition ${
                        exportOpts.resolution === r
                          ? 'bg-primary-500/20 border-primary-500/50 text-gray-900 dark:text-white'
                          : 'bg-white dark:bg-gray-900/40 border-gray-200 dark:border-gray-800 text-gray-600 dark:text-gray-400'
                      }`}>{r === '4k' ? '4K' : r.toUpperCase()}</button>
                  ))}
                </div>
                <p className="text-[10px] text-gray-500 mt-1">4K demora bem mais e gera arquivo grande (~3x)</p>
              </div>
              <div>
                <label className="block text-[11px] font-semibold text-gray-500 uppercase tracking-wider mb-2">Qualidade</label>
                <div className="grid grid-cols-4 gap-1">
                  {(() => {
                    const RES_H: Record<string, number> = { '720p': 720, '1080p': 1080, '4k': 2160 };
                    const h = RES_H[exportOpts.resolution] || 720;
                    const scale = (h * h) / (720 * 720);
                    return [
                      { v: 4,  label: 'Baixa' },
                      { v: 8,  label: 'Média' },
                      { v: 12, label: 'Alta' },
                      { v: 20, label: 'Máxima' }
                    ].map(opt => {
                      const eff = Math.min(80, Math.round(opt.v * scale));
                      return (
                        <button key={opt.v} onClick={() => setExportOpts(o => ({ ...o, bitrate: opt.v }))}
                          className={`py-2 rounded-lg text-xs font-semibold border transition ${
                            exportOpts.bitrate === opt.v
                              ? 'bg-primary-500/20 border-primary-500/50 text-gray-900 dark:text-white'
                              : 'bg-white dark:bg-gray-900/40 border-gray-200 dark:border-gray-800 text-gray-600 dark:text-gray-400'
                          }`}>
                          <div>{opt.label}</div>
                          <div className="text-[9px] opacity-60 font-normal">~{eff} Mbps</div>
                        </button>
                      );
                    });
                  })()}
                </div>
                <p className="text-[10px] text-gray-500 mt-1">Bitrate ajusta automaticamente para a resolução escolhida.</p>
              </div>
              <div>
                <label className="block text-[11px] font-semibold text-gray-500 uppercase tracking-wider mb-2">FPS</label>
                <div className="grid grid-cols-3 gap-1">
                  {([24, 30, 60] as const).map(fps => (
                    <button key={fps} onClick={() => setExportOpts(o => ({ ...o, fps }))}
                      className={`py-2 rounded-lg text-xs font-semibold border transition ${
                        exportOpts.fps === fps
                          ? 'bg-primary-500/20 border-primary-500/50 text-gray-900 dark:text-white'
                          : 'bg-white dark:bg-gray-900/40 border-gray-200 dark:border-gray-800 text-gray-600 dark:text-gray-400'
                      }`}>{fps} fps</button>
                  ))}
                </div>
              </div>
              {renderMode === 'server' ? (
                <div className="rounded-lg bg-emerald-500/10 border border-emerald-500/30 p-2 text-[11px] text-emerald-700 dark:text-emerald-300">
                  <strong>Render no servidor.</strong> Sai sempre em MP4 (pronto pra publicar) e você pode sair da tela — o vídeo aparece na galeria quando terminar. Transições wipe/zoom/blur/spin/flip/iris são simplificadas pra fade no servidor.
                </div>
              ) : (
                <div className="rounded-lg bg-amber-500/10 border border-amber-500/30 p-2 text-[11px] text-amber-700 dark:text-amber-300">
                  <strong>Render roda no seu navegador, em tempo real.</strong> Mantenha esta aba visível até o fim — em segundo plano o render pausa. Tempo: ~{Math.ceil(totalDuration)}s de render + upload.
                </div>
              )}
            </div>
            <div className="p-4 border-t border-gray-200 dark:border-gray-800 flex justify-end gap-2">
              <button onClick={() => setShowExportOptions(false)}
                className="px-3 py-2 rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-xs font-semibold text-gray-700 dark:text-gray-300">
                Cancelar
              </button>
              <button onClick={() => { setShowExportOptions(false); renderMode === 'server' ? exportOnServer() : exportProject(); }}
                className="px-4 py-2 rounded-lg bg-gradient-to-r from-primary-500 to-purple-600 text-white text-xs font-semibold inline-flex items-center gap-1.5 hover:brightness-110">
                <Sparkles className="w-3.5 h-3.5" /> Exportar agora
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Projects modal */}
      {showProjects && (
        <div className="fixed inset-0 z-[150] flex items-center justify-center p-4 bg-gray-950/80 backdrop-blur-sm" onClick={() => setShowProjects(false)}>
          <div className="w-full max-w-2xl max-h-[80vh] overflow-hidden bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-2xl shadow-2xl flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-800">
              <h2 className="text-base font-bold text-gray-900 dark:text-white inline-flex items-center gap-2">
                <FolderOpen className="w-4 h-4" /> Projetos salvos
              </h2>
              <button onClick={() => setShowProjects(false)} className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-white/5 text-gray-600 dark:text-gray-400">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="overflow-y-auto p-3 space-y-2">
              {savedProjects.length === 0 ? (
                <p className="p-8 text-center text-sm text-gray-500">Você ainda não tem projetos salvos. Crie sua timeline e clique em <strong>Salvar</strong>.</p>
              ) : savedProjects.map(p => (
                <button
                  key={p.id} onClick={() => loadProject(p.id)}
                  className="w-full flex items-center gap-3 p-3 rounded-lg bg-gray-50 dark:bg-gray-900/50 border border-gray-200 dark:border-gray-800 hover:border-primary-500/50 hover:bg-gray-100 dark:hover:bg-gray-900/80 transition text-left"
                >
                  {p.thumbnail_url ? (
                    <img src={p.thumbnail_url} alt="" className="w-16 h-10 object-cover rounded bg-black flex-shrink-0" />
                  ) : (
                    <div className="w-16 h-10 rounded bg-gray-200 dark:bg-gray-800 flex items-center justify-center flex-shrink-0"><Film className="w-4 h-4 text-gray-500" /></div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-gray-900 dark:text-white truncate">{p.name}</p>
                    <p className="text-[11px] text-gray-500">
                      {p.aspect_ratio} · {p.duration_seconds ? `${Math.round(p.duration_seconds)}s` : '—'}
                      {p.is_template && <span className="ml-1 text-amber-500">★ Modelo</span>}
                      <span className="ml-2 text-gray-400">{new Date(p.updated_at).toLocaleString('pt-BR')}</span>
                    </p>
                  </div>
                  <button onClick={(e) => removeProject(p.id, e)} className="p-1.5 rounded text-gray-400 hover:bg-rose-500/10 hover:text-rose-600">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </button>
              ))}
            </div>
            <div className="p-3 border-t border-gray-200 dark:border-gray-800 flex justify-end gap-2">
              <button
                onClick={() => saveProject(true)}
                className="px-3 py-1.5 rounded-lg bg-amber-500/10 text-amber-700 dark:text-amber-300 border border-amber-500/30 text-xs font-semibold hover:bg-amber-500/20 inline-flex items-center gap-1.5"
              >
                <Save className="w-3.5 h-3.5" /> Salvar como modelo
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Context menu */}
      {contextMenu && (
        <>
          <div className="fixed inset-0 z-[200]" onClick={() => setContextMenu(null)} onContextMenu={(e) => { e.preventDefault(); setContextMenu(null); }} />
          <div
            className="fixed z-[201] min-w-[180px] bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg shadow-2xl py-1 text-xs"
            style={{ left: contextMenu.x, top: contextMenu.y }}
          >
            <button
              onClick={() => { if (sel) seekTo((sel as any).start); setContextMenu(null); }}
              className="w-full text-left px-3 py-1.5 hover:bg-gray-100 dark:hover:bg-white/5 text-gray-900 dark:text-white"
            >Ir até o início</button>
            <button
              onClick={() => { splitAtPlayhead(); setContextMenu(null); }}
              className="w-full text-left px-3 py-1.5 hover:bg-gray-100 dark:hover:bg-white/5 text-gray-900 dark:text-white inline-flex items-center gap-2"
            ><Scissors className="w-3 h-3" /> Cortar no playhead</button>
            <button
              onClick={() => { duplicateClip(contextMenu.kind, contextMenu.id); setContextMenu(null); }}
              className="w-full text-left px-3 py-1.5 hover:bg-gray-100 dark:hover:bg-white/5 text-gray-900 dark:text-white"
            >Duplicar</button>
            {contextMenu.kind === 'video' && (
              <button
                onClick={() => { detachAudio(contextMenu.id); setContextMenu(null); }}
                className="w-full text-left px-3 py-1.5 hover:bg-gray-100 dark:hover:bg-white/5 text-gray-900 dark:text-white inline-flex items-center gap-2"
              ><Music className="w-3 h-3" /> Separar áudio</button>
            )}
            <div className="my-1 border-t border-gray-200 dark:border-gray-800" />
            <button
              onClick={() => { removeClip(contextMenu.kind, contextMenu.id); setContextMenu(null); }}
              className="w-full text-left px-3 py-1.5 hover:bg-rose-50 dark:hover:bg-rose-500/10 text-rose-700 dark:text-rose-300 inline-flex items-center gap-2"
            ><Trash2 className="w-3 h-3" /> Excluir</button>
          </div>
        </>
      )}
    </div>
  );
});

// =============================================================================
// SHORTCUTS HELP (toolbar popover)
// =============================================================================

const SHORTCUTS: { keys: string; label: string }[] = [
  { keys: 'Espaço', label: 'Tocar / pausar' },
  { keys: 'S', label: 'Cortar clipe no playhead' },
  { keys: 'M', label: 'Mutar / desmutar vídeo selecionado' },
  { keys: 'Delete', label: 'Remover clipe selecionado' },
  { keys: '← →', label: 'Mover playhead 0,1s (Shift: 1s)' },
  { keys: 'Home / End', label: 'Ir pro início / fim' },
  { keys: 'Ctrl+Z / Ctrl+Shift+Z', label: 'Desfazer / refazer' },
  { keys: 'Ctrl+C / X / V', label: 'Copiar / recortar / colar clipe' },
  { keys: 'Ctrl+S', label: 'Salvar projeto' },
  { keys: 'Ctrl+scroll', label: 'Zoom na timeline' },
  { keys: 'Arrastar ↑↓', label: 'Mudar clipe de camada' }
];

const ShortcutsHelp: React.FC = () => {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative">
      <button onClick={() => setOpen(o => !o)}
        className={`p-2 rounded hover:bg-white/10 ${open ? 'text-white bg-white/10' : 'text-gray-400 hover:text-white'}`}
        title="Atalhos de teclado">
        <Keyboard className="w-3.5 h-3.5" />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute bottom-full right-0 mb-2 z-50 w-72 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl shadow-2xl p-3">
            <p className="text-[11px] font-bold text-gray-900 dark:text-white uppercase tracking-wider mb-2 inline-flex items-center gap-1.5">
              <Keyboard className="w-3.5 h-3.5" /> Atalhos do editor
            </p>
            <div className="space-y-1">
              {SHORTCUTS.map(s => (
                <div key={s.keys} className="flex items-center justify-between gap-2 text-[11px]">
                  <span className="text-gray-600 dark:text-gray-400">{s.label}</span>
                  <kbd className="px-1.5 py-0.5 rounded bg-gray-100 dark:bg-gray-800 border border-gray-300 dark:border-gray-700 text-gray-800 dark:text-gray-200 font-mono text-[10px] whitespace-nowrap">{s.keys}</kbd>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

// =============================================================================
// PROMPT DIALOG (in-app input modal)
// =============================================================================

const PromptDialog: React.FC<{
  dialog: { title: string; body?: string; defaultValue?: string; placeholder?: string; onSubmit: (v: string) => void };
  onClose: () => void;
}> = ({ dialog, onClose }) => {
  const [value, setValue] = useState(dialog.defaultValue || '');
  const inputRef = useRef<HTMLInputElement>(null);
  useEffect(() => { setTimeout(() => inputRef.current?.select(), 50); }, []);
  const submit = () => {
    if (!value.trim()) return;
    dialog.onSubmit(value.trim());
    onClose();
  };
  return (
    <div className="fixed inset-0 z-[300] flex items-center justify-center p-4 bg-gray-950/70 backdrop-blur-sm" onClick={onClose}>
      <div className="w-full max-w-sm bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-2xl shadow-2xl overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="p-5 border-b border-gray-200 dark:border-gray-800">
          <h3 className="text-base font-bold text-gray-900 dark:text-white mb-1">{dialog.title}</h3>
          {dialog.body && <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">{dialog.body}</p>}
          <input
            ref={inputRef} type="text"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') submit(); if (e.key === 'Escape') onClose(); }}
            placeholder={dialog.placeholder}
            className="w-full px-3 py-2 rounded-lg bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-white text-sm focus:outline-none focus:border-primary-500"
          />
        </div>
        <div className="p-3 flex justify-end gap-2 bg-gray-50 dark:bg-gray-900/50">
          <button onClick={onClose}
            className="px-4 py-2 rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-sm font-semibold text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700">
            Cancelar
          </button>
          <button onClick={submit} disabled={!value.trim()}
            className="px-4 py-2 rounded-lg bg-gradient-to-r from-primary-500 to-purple-600 text-white text-sm font-semibold hover:brightness-110 disabled:opacity-50">
            OK
          </button>
        </div>
      </div>
    </div>
  );
};

// =============================================================================
// DRAGGABLE IMAGE OVERLAY (positioned in the preview)
// =============================================================================

// Free-positioning text overlay. Click-and-drag inside the preview to move.
// Keeps the same JSON state shape as the canvas exporter so what you see is
// what you get.
interface DraggableTextOverlayProps {
  clip: TextOverlayClip;
  stageHeight: number;
  currentTime: number;
  isSelected: boolean;
  onSelect: () => void;
  onChange: (patch: Partial<TextOverlayClip>) => void;
}

const DraggableTextOverlay: React.FC<DraggableTextOverlayProps> = ({ clip, stageHeight, currentTime, isSelected, onSelect, onChange }) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  // Drag state lives in refs (mutated by listeners, not state) so the listener
  // closures don't go stale across re-renders. `isDragging` toggles a useEffect
  // that owns the window listeners — it gives us proper cleanup on unmount and
  // avoids leaks if the parent removes the overlay mid-drag.
  const [isDragging, setIsDragging] = useState(false);
  const dragRef = useRef<{
    startMouseX: number; startMouseY: number;
    origX: number; origY: number;
    parentRect: DOMRect;
  } | null>(null);

  const coords = getTextCoords(clip);
  // Preview at stageHeight px should look identical to a 720-tall canvas
  // export. Both use the same scale factor relative to FONT_REFERENCE_HEIGHT.
  const fontScale = stageHeight > 0 ? stageHeight / FONT_REFERENCE_HEIGHT : 0.5;
  const renderedFontSize = clip.font_size * fontScale;
  const hasBg = !!clip.background && clip.background !== 'transparent';

  useEffect(() => {
    if (!isDragging) return;
    const onMove = (e: MouseEvent) => {
      const d = dragRef.current; if (!d) return;
      const dxPct = ((e.clientX - d.startMouseX) / d.parentRect.width) * 100;
      const dyPct = ((e.clientY - d.startMouseY) / d.parentRect.height) * 100;
      onChange({
        position_x_pct: Math.max(0, Math.min(100, d.origX + dxPct)),
        position_y_pct: Math.max(0, Math.min(100, d.origY + dyPct))
      });
    };
    const onUp = () => {
      dragRef.current = null;
      setIsDragging(false);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [isDragging, onChange]);

  const startDrag = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    onSelect();
    const parent = wrapperRef.current?.parentElement;
    if (!parent) return;
    dragRef.current = {
      startMouseX: e.clientX, startMouseY: e.clientY,
      origX: coords.x, origY: coords.y,
      parentRect: parent.getBoundingClientRect()
    };
    setIsDragging(true);
  };

  // Browsers (Chrome on desktop in particular) will start a native HTML5 drag
  // for text content if you click-and-hold. That blocks our mouseup handler
  // and gets misread as the panel "disappearing". We block it on every layer:
  // draggable={false}, onDragStart={prevent}, user-select: none, and we
  // delegate pointer events to the wrapper so the inner span never sees them.
  const blockNativeDrag = (e: React.DragEvent) => e.preventDefault();
  const tx = computeTransitionStyle(clip, currentTime, 'none');
  const composedTransform = tx.transform
    ? `translate(-50%, -50%) ${tx.transform}`
    : 'translate(-50%, -50%)';
  return (
    <div
      ref={wrapperRef}
      onMouseDown={startDrag}
      onDragStart={blockNativeDrag}
      draggable={false}
      className={`absolute cursor-move ${isSelected ? 'outline outline-2 outline-dashed outline-primary-400' : ''}`}
      style={{
        left: `${coords.x}%`,
        top: `${coords.y}%`,
        transform: composedTransform,
        transformOrigin: 'center center',
        opacity: tx.opacity,
        clipPath: tx.clipPath,
        filter: tx.filter && tx.filter !== 'none' ? tx.filter : undefined,
        zIndex: 10 + (clip.lane || 0),
        // width: max-content stops the browser from running its absolute-pos
        // shrink-to-fit algorithm, which uses (parent_width - left) as the
        // available size — that's what was making the box reflow as you
        // dragged left/right. With max-content the width is purely a function
        // of the text, so dragging only moves it, never resizes it. The
        // 90%-of-stage cap matches what the canvas exporter wraps at.
        width: 'max-content',
        maxWidth: '90%',
        userSelect: 'none',
        WebkitUserSelect: 'none'
      }}
    >
      <span
        draggable={false}
        onDragStart={blockNativeDrag}
        style={{
          color: clip.color,
          fontSize: `${renderedFontSize}px`,
          lineHeight: 1.25,
          fontFamily: `${clip.font_family}, system-ui, sans-serif`,
          fontWeight: clip.bold ? 700 : 600,
          fontStyle: clip.italic ? 'italic' : 'normal',
          background: hasBg ? clip.background : undefined,
          padding: hasBg ? `${renderedFontSize * 0.1}px ${renderedFontSize * 0.3}px` : undefined,
          borderRadius: hasBg ? `${renderedFontSize * 0.1}px` : undefined,
          textShadow: '0 2px 8px rgba(0,0,0,0.6)',
          whiteSpace: 'pre-wrap',
          textAlign: 'center',
          pointerEvents: 'none',
          userSelect: 'none',
          WebkitUserSelect: 'none',
          display: 'inline-block'
        }}>{clip.text}</span>
    </div>
  );
};

interface DraggableImageOverlayProps {
  clip: ImageOverlayClip;
  currentTime: number;
  isSelected: boolean;
  onSelect: () => void;
  onChange: (patch: Partial<ImageOverlayClip>) => void;
}

const DraggableImageOverlay: React.FC<DraggableImageOverlayProps> = ({ clip, currentTime, isSelected, onSelect, onChange }) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  type Mode = 'move' | 'resize-br' | 'resize-bl' | 'resize-tr' | 'resize-tl';
  const dragRef = useRef<{
    mode: Mode; startMouseX: number; startMouseY: number;
    origX: number; origY: number; origW: number;
    parentRect: DOMRect;
  } | null>(null);

  const startDrag = (e: React.MouseEvent, mode: Mode) => {
    e.stopPropagation();
    e.preventDefault();
    onSelect();
    const parent = wrapperRef.current?.parentElement;
    if (!parent) return;
    dragRef.current = {
      mode,
      startMouseX: e.clientX, startMouseY: e.clientY,
      origX: clip.position_x_pct, origY: clip.position_y_pct, origW: clip.width_pct,
      parentRect: parent.getBoundingClientRect()
    };
    window.addEventListener('mousemove', onDragMove);
    window.addEventListener('mouseup', onDragEnd);
  };

  const onDragMove = (e: MouseEvent) => {
    const d = dragRef.current; if (!d) return;
    const dx = e.clientX - d.startMouseX;
    const dy = e.clientY - d.startMouseY;
    const dxPct = (dx / d.parentRect.width) * 100;
    const dyPct = (dy / d.parentRect.height) * 100;

    if (d.mode === 'move') {
      onChange({
        position_x_pct: Math.max(0, Math.min(100, d.origX + dxPct)),
        position_y_pct: Math.max(0, Math.min(100, d.origY + dyPct))
      });
    } else {
      // Resize from corners — change width based on which corner pulls outward
      let newW = d.origW;
      if (d.mode === 'resize-br' || d.mode === 'resize-tr') newW = d.origW + dxPct;
      else newW = d.origW - dxPct;
      onChange({ width_pct: Math.max(5, Math.min(100, newW)) });
    }
  };

  const onDragEnd = () => {
    dragRef.current = null;
    window.removeEventListener('mousemove', onDragMove);
    window.removeEventListener('mouseup', onDragEnd);
  };

  const tx = computeTransitionStyle(clip, currentTime, 'none');
  const composedTransform = tx.transform
    ? `translate(-50%, -50%) ${tx.transform}`
    : 'translate(-50%, -50%)';
  // Multiply transition opacity into the clip's own opacity so they compose.
  const effectiveOpacity = tx.opacity != null ? clip.opacity * tx.opacity : clip.opacity;
  return (
    <div
      ref={wrapperRef}
      onMouseDown={(e) => startDrag(e, 'move')}
      className={`absolute cursor-move ${isSelected ? 'outline-2 outline-primary-400 outline-dashed outline' : ''}`}
      style={{
        width: `${clip.width_pct}%`,
        left: `${clip.position_x_pct}%`,
        top: `${clip.position_y_pct}%`,
        transform: composedTransform,
        transformOrigin: 'center center',
        opacity: effectiveOpacity,
        clipPath: tx.clipPath,
        filter: tx.filter && tx.filter !== 'none' ? tx.filter : undefined,
        zIndex: 10 + (clip.lane || 0)
      }}
    >
      <img src={clip.url} alt="" className="block w-full h-auto pointer-events-none select-none" draggable={false} />

      {/* Resize handles — only when selected */}
      {isSelected && (
        <>
          {([
            ['tl', '-top-1.5 -left-1.5 cursor-nwse-resize'],
            ['tr', '-top-1.5 -right-1.5 cursor-nesw-resize'],
            ['bl', '-bottom-1.5 -left-1.5 cursor-nesw-resize'],
            ['br', '-bottom-1.5 -right-1.5 cursor-nwse-resize']
          ] as const).map(([corner, pos]) => (
            <div key={corner}
              onMouseDown={(e) => startDrag(e, `resize-${corner}` as Mode)}
              className={`absolute w-3 h-3 bg-primary-500 border-2 border-white rounded-sm shadow ${pos}`}
            />
          ))}
        </>
      )}
    </div>
  );
};

// =============================================================================
// TIMELINE SURFACE
// =============================================================================

interface TimelineSurfaceProps {
  totalDuration: number;
  pxPerSec: number;
  currentTime: number;
  setCurrentTime: (t: number) => void;
  videoClips: VideoClip[];
  audioClips: AudioClip[];
  overlayClips: (TextOverlayClip | ImageOverlayClip)[];
  selected: Selection;
  startDrag: (e: React.MouseEvent, mode: 'move' | 'resize-left' | 'resize-right',
              kind: 'video' | 'audio' | 'overlay', clip: { id: string; start: number; duration: number; trim_start?: number }) => void;
  onBgClick: (e: React.MouseEvent) => void;
  timelineWidth: number;
  onContextMenu: (e: React.MouseEvent, kind: 'video' | 'audio' | 'overlay', id: string) => void;
  onSelectClip: (kind: 'video' | 'audio' | 'overlay', id: string) => void;
  draggingId: string | null;
  snapLine: number | null;
  playing: boolean;
  onZoomChange: (pxPerSec: number) => void;
}

// React.memo: as props agora são estáveis fora do tick do playhead; o re-render
// por currentTime ainda acontece (playhead/auto-scroll), mas os filhos
// memoizados (ClipBlock/Ruler/grid) não pagam o custo junto.
const TimelineSurface = React.memo<TimelineSurfaceProps>(({
  totalDuration, pxPerSec, currentTime, setCurrentTime,
  videoClips, audioClips, overlayClips, selected, startDrag, onBgClick, timelineWidth,
  onContextMenu, onSelectClip, draggingId, snapLine, playing, onZoomChange
}) => {
  // Unified visual track: videos + overlays share lanes (V1, V2, V3...)
  const visualLanes = lanesUsed(videoClips, overlayClips);
  const audioLanes = lanesUsed(audioClips);
  const visualTrackHeight = TRACK_HEIGHT * visualLanes;
  const audioTrackHeight = TRACK_HEIGHT * audioLanes;
  const audioTop = RULER_HEIGHT + visualTrackHeight;
  const totalHeight = audioTop + audioTrackHeight;
  const surfaceRef = useRef<HTMLDivElement>(null);

  // Scrub throttle: mousemove dispara 120+x/seg em mouses modernos. Sem throttle,
  // cada movimento chama setCurrentTime → re-render completo do TimelineEditor +
  // recálculo de activeOverlays/activeCaptions + re-render dos <video> com novos
  // filter/transform. Resultado: trava ao arrastar o playhead.
  // Solução: usar requestAnimationFrame pra coalescer múltiplos mousemoves em 1
  // setCurrentTime por frame (~60fps cap).
  const scrubRafRef = useRef<number | null>(null);
  const scrubPendingXRef = useRef(0);
  // dimsRef: mantém flushScrub/onRulerMouseDown estáveis (Ruler é memoizado)
  const dimsRef = useRef({ totalDuration, pxPerSec });
  useEffect(() => { dimsRef.current = { totalDuration, pxPerSec }; });
  const flushScrub = useCallback(() => {
    scrubRafRef.current = null;
    const el = surfaceRef.current; if (!el) return;
    const rect = el.getBoundingClientRect();
    const x = scrubPendingXRef.current - rect.left;
    const { totalDuration, pxPerSec } = dimsRef.current;
    setCurrentTime(Math.max(0, Math.min(totalDuration, x / pxPerSec)));
  }, [setCurrentTime]);
  const seekFromMouseEvent = useCallback((clientX: number) => {
    scrubPendingXRef.current = clientX;
    if (scrubRafRef.current != null) return; // já agendado pra próximo frame
    scrubRafRef.current = requestAnimationFrame(flushScrub);
  }, [flushScrub]);

  // Auto-scroll: durante a reprodução, mantém o playhead visível rolando a
  // timeline sozinho (antes o playhead sumia da tela em vídeos mais longos).
  const scrollRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!playing) return;
    const el = scrollRef.current; if (!el) return;
    const x = currentTime * pxPerSec;
    if (x < el.scrollLeft + 24 || x > el.scrollLeft + el.clientWidth - 80) {
      el.scrollLeft = Math.max(0, x - 100);
    }
  }, [currentTime, playing, pxPerSec]);

  // Ctrl/Cmd + scroll = zoom ancorado no cursor (padrão de qualquer editor).
  // Listener manual porque onWheel do React é passivo e não deixa preventDefault.
  useEffect(() => {
    const el = scrollRef.current; if (!el) return;
    const onWheel = (e: WheelEvent) => {
      if (!(e.ctrlKey || e.metaKey)) return;
      e.preventDefault();
      const rect = el.getBoundingClientRect();
      const offsetX = e.clientX - rect.left;
      const timeAtCursor = (el.scrollLeft + offsetX) / pxPerSec;
      const next = Math.max(15, Math.min(200, pxPerSec + (e.deltaY < 0 ? 10 : -10)));
      if (next === pxPerSec) return;
      onZoomChange(next);
      // Reancora o scroll depois do re-render pra manter o mesmo instante sob o cursor
      requestAnimationFrame(() => { el.scrollLeft = Math.max(0, timeAtCursor * next - offsetX); });
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [pxPerSec, onZoomChange]);

  const playheadDragRef = useRef(false);
  const onRulerMouseDown = useCallback((e: React.MouseEvent) => {
    playheadDragRef.current = true;
    seekFromMouseEvent(e.clientX);
    const onMove = (ev: MouseEvent) => playheadDragRef.current && seekFromMouseEvent(ev.clientX);
    const onUp = () => {
      playheadDragRef.current = false;
      if (scrubRafRef.current != null) { cancelAnimationFrame(scrubRafRef.current); scrubRafRef.current = null; }
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }, [seekFromMouseEvent]);

  // Grid não depende do playhead — sem o useMemo era recriado a cada tick
  const gridLines = useMemo(() => {
    const secs = Math.ceil(totalDuration) + 1;
    const step = Math.max(1, Math.ceil(secs / MAX_GRID_LINES));
    return Array.from({ length: Math.ceil(secs / step) }).map((_, i) => (
      <div key={i} className="absolute top-0 bottom-0 w-px bg-gray-200/30 dark:bg-gray-800/40 pointer-events-none"
        style={{ left: `${i * step * pxPerSec}px` }} />
    ));
  }, [totalDuration, pxPerSec]);

  return (
    <div className="flex">
      {/* Left header column */}
      <div className="flex-shrink-0 bg-gray-50 dark:bg-gray-900/60 border-r border-gray-200 dark:border-gray-800" style={{ width: TRACK_HEADER_WIDTH }}>
        <div style={{ height: RULER_HEIGHT }} className="border-b border-gray-200 dark:border-gray-800" />
        <TrackHeader icon={<Film className="w-3.5 h-3.5" />} label="Vídeo + Overlays" color="text-cyan-700 dark:text-cyan-300" height={visualTrackHeight} lanes={visualLanes} />
        <TrackHeader icon={<Music className="w-3.5 h-3.5" />} label="Áudio" color="text-emerald-700 dark:text-emerald-300" height={audioTrackHeight} lanes={audioLanes} />
      </div>

      {/* Scrollable timeline area */}
      <div ref={scrollRef} className="flex-1 overflow-x-auto">
        {/* Use onMouseDown for deselection: clicking a ClipBlock fires mousedown
            (handler stops propagation, surface never sees it). Using onClick
            instead races with the post-mouseup state churn — a click that
            lands after a clip's `top` has updated retargets to the surface
            and deselects the very block the user just clicked. */}
        <div ref={surfaceRef} className="relative" style={{ width: timelineWidth, minHeight: totalHeight }} onMouseDown={onBgClick}>
          {/* Ruler */}
          <Ruler total={totalDuration} pxPerSec={pxPerSec} onMouseDown={onRulerMouseDown} />

          {/* Track lane backgrounds — visual track (videos+overlays unified) + audio.
              pointer-events-none pra clique em área vazia chegar na surface (seek/deselect). */}
          <div className="absolute left-0 right-0 border-b border-gray-200 dark:border-gray-800/60 pointer-events-none" style={{ top: RULER_HEIGHT, height: visualTrackHeight, background: 'rgba(8,145,178,0.04)' }} />
          {/* Lane separator lines inside visual track */}
          {Array.from({ length: visualLanes - 1 }).map((_, i) => (
            <div key={`vsep-${i}`} className="absolute left-0 right-0 border-t border-dashed border-gray-300/40 dark:border-gray-700/40 pointer-events-none"
              style={{ top: RULER_HEIGHT + (i + 1) * TRACK_HEIGHT }} />
          ))}
          <div className="absolute left-0 right-0 pointer-events-none" style={{ top: audioTop, height: audioTrackHeight, background: 'rgba(16,185,129,0.04)' }} />
          {Array.from({ length: audioLanes - 1 }).map((_, i) => (
            <div key={`asep-${i}`} className="absolute left-0 right-0 border-t border-dashed border-gray-300/40 dark:border-gray-700/40 pointer-events-none"
              style={{ top: audioTop + (i + 1) * TRACK_HEIGHT }} />
          ))}

          {/* Grid lines — 1 por segundo, mas com passo maior quando a timeline
              é longa (senão vira dezenas de milhares de divs e trava a aba). */}
          {gridLines}

          {/* Video clips (in unified visual track, lane = stacking order) */}
          {videoClips.map(clip => (
            <ClipBlock key={clip.id} top={RULER_HEIGHT + (clip.lane || 0) * TRACK_HEIGHT + 4}
              clip={clip} kind="video" pxPerSec={pxPerSec}
              colorClass="bg-gradient-to-r from-cyan-500/40 to-blue-500/40 border-cyan-500/60"
              selected={selected?.kind === 'video' && selected.id === clip.id}
              label={`V${(clip.lane || 0) + 1} · Vídeo`}
              thumbUrl={clip.thumbnail_url}
              startDrag={startDrag}
              onContextMenu={onContextMenu}
              onSelect={onSelectClip}
              dragging={draggingId === clip.id}
            />
          ))}

          {/* Overlay clips (same visual track, also use lane) */}
          {overlayClips.map(clip => (
            <ClipBlock key={clip.id} top={RULER_HEIGHT + (clip.lane || 0) * TRACK_HEIGHT + 4}
              clip={clip} kind="overlay" pxPerSec={pxPerSec}
              colorClass={clip.kind === 'text'
                ? "bg-gradient-to-r from-violet-500/40 to-purple-500/40 border-violet-500/60"
                : "bg-gradient-to-r from-amber-500/40 to-orange-500/40 border-amber-500/60"}
              selected={selected?.kind === 'overlay' && selected.id === clip.id}
              label={clip.kind === 'text'
                ? `V${(clip.lane || 0) + 1} · T: ${(clip as TextOverlayClip).text.slice(0, 14)}`
                : `V${(clip.lane || 0) + 1} · Imagem`}
              startDrag={startDrag}
              onContextMenu={onContextMenu}
              onSelect={onSelectClip}
              dragging={draggingId === clip.id}
            />
          ))}

          {/* Audio clips */}
          {audioClips.map(clip => (
            <ClipBlock key={clip.id} top={audioTop + (clip.lane || 0) * TRACK_HEIGHT + 4}
              clip={clip} kind="audio" pxPerSec={pxPerSec}
              colorClass="bg-gradient-to-r from-emerald-500/40 to-green-500/40 border-emerald-500/60"
              selected={selected?.kind === 'audio' && selected.id === clip.id}
              label={`A${(clip.lane || 0) + 1} · ♪ ${clip.name.slice(0, 22)}`}
              startDrag={startDrag}
              onContextMenu={onContextMenu}
              onSelect={onSelectClip}
              dragging={draggingId === clip.id}
            />
          ))}

          {/* Snap indicator (amber line) — visible only while dragging and snapped */}
          {snapLine !== null && (
            <div className="absolute top-0 bottom-0 w-0.5 bg-amber-400 pointer-events-none z-40 shadow-[0_0_8px_rgba(251,191,36,0.8)]"
              style={{ left: `${snapLine * pxPerSec}px` }}>
              <div className="absolute top-0 -left-[3px] w-2 h-2 rounded-full bg-amber-400" />
              <div className="absolute bottom-0 -left-[3px] w-2 h-2 rounded-full bg-amber-400" />
            </div>
          )}

          {/* Playhead */}
          <div className="absolute top-0 bottom-0 w-0.5 bg-rose-500 pointer-events-none z-30"
            style={{ left: `${currentTime * pxPerSec}px` }}>
            <div className="absolute -top-1 -left-[5px] w-3 h-3 bg-rose-500 rotate-45" />
          </div>
        </div>
      </div>
    </div>
  );
});

// =============================================================================
// SUB-COMPONENTS
// =============================================================================

const TrackHeader: React.FC<{ icon: React.ReactNode; label: string; color: string; height: number; lanes: number }> = ({ icon, label, color, height, lanes }) => (
  <div className="border-b border-gray-200 dark:border-gray-800/60 flex flex-col justify-center items-start gap-0.5 px-2" style={{ height }}>
    <div className="flex items-center gap-1.5">
      <span className={color}>{icon}</span>
      <span className={`text-[10px] font-semibold ${color}`}>{label}</span>
    </div>
    {lanes > 1 && <span className="text-[9px] text-gray-500">{lanes} camadas</span>}
  </div>
);

// memo: a régua só depende de duração/zoom — não re-renderiza a cada tick
const Ruler = React.memo<{ total: number; pxPerSec: number; onMouseDown: (e: React.MouseEvent) => void }>(({ total, pxPerSec, onMouseDown }) => {
  const base = pxPerSec >= 80 ? 1 : pxPerSec >= 30 ? 5 : 10;
  // Aumenta o intervalo se o número de ticks passar do teto (timeline longa)
  const tickInterval = Math.max(base, Math.ceil((Math.ceil(total) + 1) / MAX_GRID_LINES));
  const ticks: number[] = [];
  for (let i = 0; i <= Math.ceil(total); i += tickInterval) ticks.push(i);
  return (
    <div
      className="absolute top-0 left-0 right-0 border-b border-gray-200 dark:border-gray-800 bg-gray-100 dark:bg-gray-900/80 cursor-pointer select-none"
      style={{ height: RULER_HEIGHT }}
      onMouseDown={onMouseDown}
    >
      {ticks.map(tick => (
        <div key={tick} className="absolute top-0 bottom-0" style={{ left: `${tick * pxPerSec}px` }}>
          <div className="absolute top-0 left-0 w-px h-2.5 bg-gray-400 dark:bg-gray-600" />
          <span className="absolute top-3 left-1 text-[9px] text-gray-500 leading-3 tabular-nums">{tick}s</span>
        </div>
      ))}
    </div>
  );
});

interface ClipBlockProps {
  top: number;
  clip: { id: string; start: number; duration: number; trim_start?: number; lane?: number };
  kind: 'video' | 'audio' | 'overlay';
  pxPerSec: number;
  colorClass: string;
  selected: boolean;
  label: string;
  thumbUrl?: string;
  startDrag: (e: React.MouseEvent, mode: 'move' | 'resize-left' | 'resize-right',
              kind: 'video' | 'audio' | 'overlay', clip: { id: string; start: number; duration: number; trim_start?: number; lane?: number }) => void;
  onContextMenu?: (e: React.MouseEvent, kind: 'video' | 'audio' | 'overlay', id: string) => void;
  onSelect?: (kind: 'video' | 'audio' | 'overlay', id: string) => void;
  dragging?: boolean;
}

// React.memo: os blocos da timeline não dependem do playhead — sem o memo, cada
// tick do currentTime (10x/s tocando, 60x/s no scrub/drag) re-renderizava TODOS
// os blocos. Com props estáveis, só o clipe que realmente mudou re-renderiza.
const ClipBlock = React.memo<ClipBlockProps>(({
  top, clip, kind, pxPerSec, colorClass, selected, label, thumbUrl,
  startDrag, onContextMenu, onSelect, dragging
}) => {
  const left = clip.start * pxPerSec;
  const width = Math.max(20, clip.duration * pxPerSec);
  return (
    <div
      className={`absolute rounded-md border ${colorClass} overflow-hidden flex items-center text-[10px] font-semibold transition-shadow ${
        selected ? 'ring-2 ring-white shadow-lg shadow-black/40' : ''
      } ${dragging ? 'opacity-80 ring-4 ring-primary-400/60 shadow-2xl scale-[1.02] z-40' : ''}`}
      style={{ top, left, width, height: TRACK_HEIGHT - 8 }}
      onMouseDown={(e) => startDrag(e, 'move', kind, clip)}
      onClick={(e) => { e.stopPropagation(); onSelect?.(kind, clip.id); }}
      onContextMenu={(e) => onContextMenu?.(e, kind, clip.id)}
    >
      {thumbUrl && (
        <img src={thumbUrl} alt="" className="absolute inset-0 w-full h-full object-cover opacity-30 pointer-events-none" />
      )}
      <span className="relative px-2 truncate text-white drop-shadow z-10 flex-1 cursor-grab active:cursor-grabbing">{label}</span>
      <span className="relative text-white/70 text-[9px] pr-2 z-10 tabular-nums">{clip.duration.toFixed(1)}s</span>
      <div className="absolute left-0 top-0 bottom-0 w-1.5 cursor-ew-resize hover:bg-white/40 z-20" onMouseDown={(e) => startDrag(e, 'resize-left', kind, clip)} title="Trim início" />
      <div className="absolute right-0 top-0 bottom-0 w-1.5 cursor-ew-resize hover:bg-white/40 z-20" onMouseDown={(e) => startDrag(e, 'resize-right', kind, clip)} title="Esticar fim" />
    </div>
  );
});

// =============================================================================
// PROPERTIES PANEL
// =============================================================================

// memo: com onChange/onRemove/autoCaptionButton estáveis no pai, o painel não
// re-renderiza a cada tick do playhead — só quando o clipe selecionado muda.
const ClipProperties = React.memo<{
  clip: AnyClip;
  kind: 'video' | 'audio' | 'overlay';
  onChange: (patch: any) => void;
  onRemove: () => void;
  autoCaptionButton?: React.ReactNode;
}>(({ clip, kind, onChange, onRemove, autoCaptionButton }) => (
  <div className="space-y-3">
    <div className="grid grid-cols-2 gap-2">
      <NumberField label="Início (s)" value={clip.start} step={0.1} min={0} onChange={(v) => onChange({ start: v })} />
      <NumberField label="Duração (s)" value={clip.duration} step={0.1} min={0.1} onChange={(v) => onChange({ duration: v })} />
    </div>

    {kind === 'video' && (
      <>
        <TransitionPicker
          value={(clip as VideoClip).transition || 'cut'}
          duration={(clip as VideoClip).transition_duration ?? 0.5}
          onChange={(t) => onChange({ transition: t })}
          onDurationChange={(d) => onChange({ transition_duration: d })}
        />

        {/* Auto-caption from audio */}
        {autoCaptionButton}
        <div>
          <label className="block text-[11px] font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Áudio do vídeo</label>
          <button onClick={() => onChange({ muted: !(clip as VideoClip).muted })}
            className={`w-full inline-flex items-center justify-center gap-1.5 py-1.5 rounded-lg text-xs font-semibold border transition ${
              (clip as VideoClip).muted
                ? 'bg-rose-500/20 border-rose-500/40 text-rose-700 dark:text-rose-300'
                : 'bg-emerald-500/20 border-emerald-500/40 text-emerald-700 dark:text-emerald-300'
            }`}>
            {(clip as VideoClip).muted ? <><VolumeX className="w-3.5 h-3.5" /> Mudo</> : <><Volume2 className="w-3.5 h-3.5" /> Com som</>}
          </button>
          <input type="range" min={0} max={1} step={0.05} value={(clip as VideoClip).volume}
            onChange={(e) => onChange({ volume: Number(e.target.value) })}
            className="w-full mt-2" disabled={(clip as VideoClip).muted} />
        </div>
        <FilterPicker value={(clip as VideoClip).filter || 'none'} onChange={(f) => onChange({ filter: f })} />
      </>
    )}

    {kind === 'audio' && (
      <>
        <p className="text-[11px] text-gray-500 truncate">{(clip as AudioClip).name}</p>
        <div>
          <label className="block text-[11px] font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Volume</label>
          <input type="range" min={0} max={1} step={0.05} value={(clip as AudioClip).volume}
            onChange={(e) => onChange({ volume: Number(e.target.value) })} className="w-full" />
        </div>
      </>
    )}

    {kind === 'overlay' && (clip.kind === 'text' || clip.kind === 'image') && (
      <TransitionPicker
        value={(clip as TextOverlayClip | ImageOverlayClip).transition || 'cut'}
        duration={(clip as TextOverlayClip | ImageOverlayClip).transition_duration ?? 0.5}
        onChange={(t) => onChange({ transition: t })}
        onDurationChange={(d) => onChange({ transition_duration: d })}
      />
    )}

    {kind === 'overlay' && clip.kind === 'text' && (
      <TextOverlayProps clip={clip as TextOverlayClip} onChange={onChange} />
    )}
    {kind === 'overlay' && clip.kind === 'image' && (
      <ImageOverlayProps clip={clip as ImageOverlayClip} onChange={onChange} />
    )}

    <button onClick={onRemove}
      className="w-full inline-flex items-center justify-center gap-1.5 py-2 rounded-lg bg-rose-500/10 border border-rose-500/30 text-rose-700 dark:text-rose-300 text-xs font-semibold hover:bg-rose-500/20 transition mt-2">
      <Trash2 className="w-3.5 h-3.5" /> Remover (Del)
    </button>
  </div>
));

const TextOverlayProps: React.FC<{ clip: TextOverlayClip; onChange: (p: any) => void }> = ({ clip, onChange }) => {
  const { t } = useTranslation();
  const bg = parseBackground(clip.background);
  return (
    <>
      <div>
        <label className="block text-[11px] font-semibold text-gray-500 uppercase tracking-wider mb-1.5">{t('videoStudio.editor.textOverlay')}</label>
        <textarea value={clip.text} onChange={(e) => onChange({ text: e.target.value })} rows={2}
          className="w-full px-2 py-1.5 rounded-lg bg-white dark:bg-gray-900/60 border border-gray-200 dark:border-gray-800 text-gray-900 dark:text-white text-sm resize-none" />
      </div>

      <div>
        <label className="block text-[11px] font-semibold text-gray-500 uppercase tracking-wider mb-1.5">{t('videoStudio.editor.text.quickStyles')}</label>
        <div className="grid grid-cols-3 gap-1">
          {TEXT_PRESETS.map(p => {
            const label = t(`videoStudio.editor.text.${p.key}`) as string;
            return (
              <button key={p.key} onClick={() => onChange(p.props)}
                title={t('videoStudio.editor.text.applyPreset', { label }) as string}
                className="py-1.5 rounded-lg text-[10px] font-semibold border transition bg-white dark:bg-gray-900/40 border-gray-200 dark:border-gray-800 text-gray-700 dark:text-gray-300 hover:bg-primary-500/10 hover:border-primary-500/40">
                {label}
              </button>
            );
          })}
        </div>
      </div>

      <div>
        <label className="block text-[11px] font-semibold text-gray-500 uppercase tracking-wider mb-1.5">{t('videoStudio.editor.text.positionHint')}</label>
        <div className="grid grid-cols-3 gap-1">
          {([
            { p: 'top' as const,    y: 10, key: 'positionTop' },
            { p: 'center' as const, y: 50, key: 'positionCenter' },
            { p: 'bottom' as const, y: 88, key: 'positionBottom' }
          ]).map(({ p, y, key }) => {
            const Icon = p === 'top' ? AlignLeft : p === 'center' ? AlignCenter : AlignRight;
            const active = Math.abs((clip.position_y_pct ?? (clip.position === 'top' ? 10 : clip.position === 'center' ? 50 : 90)) - y) < 5;
            return (
              <button key={p} onClick={() => onChange({ position: p, position_x_pct: 50, position_y_pct: y })}
                title={t(`videoStudio.editor.${key}`) as string}
                className={`py-1.5 rounded-lg text-xs font-semibold border transition flex items-center justify-center gap-1 ${
                  active ? 'bg-primary-500/20 border-primary-500/50 text-gray-900 dark:text-white' : 'bg-white dark:bg-gray-900/40 border-gray-200 dark:border-gray-800 text-gray-600 dark:text-gray-400'
                }`}>
                <Icon className="w-3.5 h-3.5" />
              </button>
            );
          })}
        </div>
      </div>

      <div>
        <label className="block text-[11px] font-semibold text-gray-500 uppercase tracking-wider mb-1.5">{t('videoStudio.editor.look')}</label>
        <select value={clip.font_family} onChange={(e) => onChange({ font_family: e.target.value as FontFamily })}
          className="w-full px-2 py-1.5 rounded-lg bg-white dark:bg-gray-900/60 border border-gray-200 dark:border-gray-800 text-gray-900 dark:text-white text-xs">
          {FONT_FAMILIES.map(f => <option key={f} value={f} style={{ fontFamily: f }}>{f}</option>)}
        </select>
        <div className="grid grid-cols-3 gap-2 mt-2">
          <input type="number" min={20} max={140} value={clip.font_size} onChange={(e) => onChange({ font_size: Number(e.target.value) })}
            className="px-2 py-1.5 rounded-lg bg-white dark:bg-gray-900/60 border border-gray-200 dark:border-gray-800 text-gray-900 dark:text-white text-xs" />
          <button onClick={() => onChange({ bold: !clip.bold })} className={`py-1.5 rounded-lg border text-xs font-bold ${clip.bold ? 'bg-primary-500/20 border-primary-500/50 text-gray-900 dark:text-white' : 'bg-white dark:bg-gray-900/40 border-gray-200 dark:border-gray-800 text-gray-600 dark:text-gray-400'}`}><Bold className="w-3.5 h-3.5 inline" /></button>
          <button onClick={() => onChange({ italic: !clip.italic })} className={`py-1.5 rounded-lg border text-xs ${clip.italic ? 'bg-primary-500/20 border-primary-500/50 text-gray-900 dark:text-white italic' : 'bg-white dark:bg-gray-900/40 border-gray-200 dark:border-gray-800 text-gray-600 dark:text-gray-400'}`}><Italic className="w-3.5 h-3.5 inline" /></button>
        </div>
      </div>

      <div>
        <label className="block text-[11px] font-semibold text-gray-500 uppercase tracking-wider mb-1">{t('videoStudio.editor.text.color')}</label>
        <div className="flex items-center gap-2">
          <input type="color" value={clip.color} onChange={(e) => onChange({ color: e.target.value.toUpperCase() })}
            className="w-10 h-8 rounded bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 cursor-pointer" />
          <div className="flex-1 grid grid-cols-8 gap-1">
            {COLOR_SWATCHES.map(c => (
              <button key={c} onClick={() => onChange({ color: c })}
                title={c} aria-label={t('videoStudio.editor.text.swatchColor', { hex: c }) as string}
                className={`h-6 rounded border transition ${clip.color.toUpperCase() === c ? 'border-primary-500 ring-2 ring-primary-500/40' : 'border-gray-300 dark:border-gray-700 hover:border-primary-500/60'}`}
                style={{ backgroundColor: c }} />
            ))}
          </div>
        </div>
      </div>

      <div>
        <div className="flex items-center justify-between mb-1.5">
          <label className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider">{t('videoStudio.editor.text.background')}</label>
          <button onClick={() => onChange({ background: bg.enabled ? 'transparent' : buildBackground(bg.hex, bg.opacity) })}
            className={`text-[10px] font-semibold px-2 py-0.5 rounded border transition ${
              bg.enabled
                ? 'bg-primary-500/20 border-primary-500/50 text-gray-900 dark:text-white'
                : 'bg-white dark:bg-gray-900/40 border-gray-200 dark:border-gray-800 text-gray-500'
            }`}>
            {bg.enabled ? t('videoStudio.editor.text.backgroundOn') : t('videoStudio.editor.text.backgroundOff')}
          </button>
        </div>
        {bg.enabled && (
          <>
            <div className="flex items-center gap-2">
              <input type="color" value={bg.hex} onChange={(e) => onChange({ background: buildBackground(e.target.value, bg.opacity) })}
                className="w-10 h-8 rounded bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 cursor-pointer" />
              <div className="flex-1 grid grid-cols-8 gap-1">
                {COLOR_SWATCHES.map(c => (
                  <button key={c} onClick={() => onChange({ background: buildBackground(c, bg.opacity) })}
                    title={c} aria-label={t('videoStudio.editor.text.swatchBackground', { hex: c }) as string}
                    className={`h-6 rounded border transition ${bg.hex === c ? 'border-primary-500 ring-2 ring-primary-500/40' : 'border-gray-300 dark:border-gray-700 hover:border-primary-500/60'}`}
                    style={{ backgroundColor: c }} />
                ))}
              </div>
            </div>
            <div className="mt-2">
              <div className="flex items-center justify-between text-[10px] text-gray-500 mb-1">
                <span>{t('videoStudio.editor.text.opacity')}</span>
                <span className="tabular-nums">{Math.round(bg.opacity * 100)}%</span>
              </div>
              <input type="range" min={0} max={1} step={0.05} value={bg.opacity}
                onChange={(e) => onChange({ background: buildBackground(bg.hex, Number(e.target.value)) })}
                className="w-full" />
            </div>
          </>
        )}
      </div>
    </>
  );
};

const ImageOverlayProps: React.FC<{ clip: ImageOverlayClip; onChange: (p: any) => void }> = ({ clip, onChange }) => (
  <>
    <img src={clip.url} alt="" className="w-full max-h-32 object-contain rounded bg-gray-100 dark:bg-gray-800" />
    <NumberField label="Largura (% da tela)" value={clip.width_pct} step={1} min={5} max={100} onChange={(v) => onChange({ width_pct: v })} />
    <div className="grid grid-cols-2 gap-2">
      <NumberField label="Posição X (%)" value={clip.position_x_pct} step={1} min={0} max={100} onChange={(v) => onChange({ position_x_pct: v })} />
      <NumberField label="Posição Y (%)" value={clip.position_y_pct} step={1} min={0} max={100} onChange={(v) => onChange({ position_y_pct: v })} />
    </div>
    <div>
      <label className="block text-[11px] font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Opacidade</label>
      <input type="range" min={0} max={1} step={0.05} value={clip.opacity}
        onChange={(e) => onChange({ opacity: Number(e.target.value) })} className="w-full" />
    </div>
  </>
);

const FilterPicker: React.FC<{ value: string; onChange: (v: string) => void }> = ({ value, onChange }) => (
  <div>
    <label className="block text-[11px] font-semibold text-gray-500 uppercase tracking-wider mb-1.5 flex items-center gap-1">
      <Palette className="w-3 h-3" /> Filtro de cor
    </label>
    <div className="grid grid-cols-3 gap-1">
      {FILTER_PRESETS.map(p => (
        <button key={p.id} onClick={() => onChange(p.id)}
          className={`py-1.5 rounded-lg text-[10px] font-semibold border transition ${
            value === p.id
              ? 'bg-primary-500/20 border-primary-500/50 text-gray-900 dark:text-white'
              : 'bg-white dark:bg-gray-900/40 border-gray-200 dark:border-gray-800 text-gray-600 dark:text-gray-400'
          }`}>
          {p.label}
        </button>
      ))}
    </div>
  </div>
);

// min/max do HTML são só dica visual — o browser deixa digitar qualquer coisa.
// Sem clampar aqui, "Duração = 99999999" fazia a timeline renderizar milhões de
// linhas de grade e a aba travava; campo vazio virava Number('') = 0 e sumia com
// o clipe. Por isso o valor é editado como texto e só aplicado quando é válido.
const NumberField: React.FC<{ label: string; value: number; step?: number; min?: number; max?: number; onChange: (v: number) => void }> = ({ label, value, step = 1, min, max, onChange }) => {
  const [draft, setDraft] = useState<string | null>(null);
  const lo = min ?? -MAX_TIMELINE_SECONDS;
  const hi = max ?? MAX_TIMELINE_SECONDS;
  const commit = (raw: string) => {
    setDraft(null);
    const n = Number(raw);
    if (raw.trim() === '' || !Number.isFinite(n)) return; // mantém o valor anterior
    onChange(Math.max(lo, Math.min(hi, n)));
  };
  return (
    <div>
      <label className="block text-[11px] font-semibold text-gray-500 uppercase tracking-wider mb-1">{label}</label>
      <input type="number" step={step} min={min} max={max}
        value={draft ?? String(Number(value.toFixed(2)))}
        onChange={(e) => {
          setDraft(e.target.value);
          const n = Number(e.target.value);
          // Aplica ao vivo só quando já está dentro da faixa (digitação parcial
          // como "" ou "-" fica só no rascunho até o blur).
          if (e.target.value.trim() !== '' && Number.isFinite(n) && n >= lo && n <= hi) onChange(n);
        }}
        onBlur={(e) => commit(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') commit((e.target as HTMLInputElement).value); }}
        className="w-full px-2 py-1.5 rounded-lg bg-white dark:bg-gray-900/60 border border-gray-200 dark:border-gray-800 text-gray-900 dark:text-white text-xs tabular-nums" />
    </div>
  );
};

// "Salvo há X" indicator. `tick` is just a re-render trigger so the relative time
// updates without us having to hold real wall-clock state.
const SaveIndicator: React.FC<{
  projectId: string | null;
  saving: boolean;
  autoSaving: boolean;
  lastSavedAt: number | null;
  tick: number;
}> = ({ projectId, saving, autoSaving, lastSavedAt, tick }) => {
  // Reading `tick` so React re-renders when the parent's interval fires.
  void tick;

  if (saving) {
    return (
      <span className="text-[10px] text-emerald-600 dark:text-emerald-400 inline-flex items-center gap-1">
        <Loader2 className="w-3 h-3 animate-spin" /> Salvando…
      </span>
    );
  }
  if (autoSaving) {
    return (
      <span className="text-[10px] text-emerald-600 dark:text-emerald-400 inline-flex items-center gap-1">
        <Loader2 className="w-3 h-3 animate-spin" /> Auto-salvando…
      </span>
    );
  }
  if (!projectId) {
    return (
      <span className="text-[10px] text-amber-600 dark:text-amber-400 inline-flex items-center gap-1">
        <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse" /> não salvo
      </span>
    );
  }
  if (!lastSavedAt) {
    return (
      <span className="text-[10px] text-emerald-600 dark:text-emerald-400 inline-flex items-center gap-1">
        <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" /> salvo
      </span>
    );
  }
  const diffSec = Math.floor((Date.now() - lastSavedAt) / 1000);
  let label: string;
  if (diffSec < 5)        label = 'salvo agora';
  else if (diffSec < 60)  label = `salvo há ${diffSec}s`;
  else if (diffSec < 3600) label = `salvo há ${Math.floor(diffSec / 60)} min`;
  else                    label = `salvo às ${new Date(lastSavedAt).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}`;
  return (
    <span className="text-[10px] text-emerald-600 dark:text-emerald-400 inline-flex items-center gap-1" title={new Date(lastSavedAt).toLocaleString('pt-BR')}>
      <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" /> {label}
    </span>
  );
};

TimelineEditor.displayName = 'TimelineEditor';

export default TimelineEditor;
